import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StaffMember, UserRole, BusinessHours, WeeklyRoster, Shift, Weekday, ShiftStatus, RosterStatus, CurrentUser, TimesheetEntry, BusinessInfo, PayslipRecord } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import StaffMemberModal from '../components/staff/StaffMemberModal';
import RosterQuarterHourTime from '../components/staff/RosterQuarterHourTime';
import { rosterService, timesheetService, payslipService } from '../lib/database';
import { getTimesheetLaborSummary, getWeekStartTimestamp } from '../lib/reports';
import type { RosterShiftForReport, TimesheetEntryForReport } from '../lib/reports';
import { calculatePaye, type PayFrequency } from '../lib/nzPaye';
import { useSoftKeyboardField } from '../hooks/useSoftKeyboardField';
import { generatePayslipPdf, generatePayslipsBatchPdf } from '../lib/payslipPdf';
import { emailService } from '../lib/email';
import {
  clampUnpaidMealMinutes,
  DEFAULT_UNPAID_MEAL_MINUTES,
  MAX_UNPAID_MEAL_MINUTES,
  MIN_UNPAID_MEAL_MINUTES,
  ensureMealStartsSuggested,
  firstRosterMealValidationError,
  isOvernightShift,
  normalizeShiftMealFields,
  nzBreakComplianceHint,
  nzTenPlusMealWarning,
  paidHoursRounded,
  requiresUnpaidMeal,
  shiftMealValidationError,
  shiftWallMinutes,
  suggestMealBreakStart,
  wallHoursRounded,
} from '../lib/nzRosterBreaks';
import { computeRosterCostByWeekday, getStaffRosterKey, isRosterEligibleStaff } from '../lib/rosterCostByDay';
import { resolveDayPeriods } from '../lib/businessHours';
import { ensureUniqueShiftIds, mergeExternalRosterShifts, newRosterShiftId } from '../lib/rosterMerge';
import { exportRosterExcel, exportRosterPdf, rosterDayLabels } from '../lib/rosterExport';
import {
  applyRosterJsonToWeek,
  buildRosterJsonDocument,
  downloadRosterJson,
  readRosterJsonFile,
} from '../lib/rosterJsonTransfer';
import {
  groupStaffByRosterRole,
  resolveStaffRoleName,
  rosterRoleCategory,
} from '../lib/rosterRoleOrder';

interface StaffManagementPageProps {
  staffMembers: StaffMember[];
  userRoles: UserRole[];
  onSave: (member: StaffMember) => void | Promise<void>;
  onDelete: (memberId: number) => void;
  onSendInvitation?: (email: string) => Promise<void>;
  businessHours?: BusinessHours;
  currentUser?: CurrentUser;
  businessInfo?: BusinessInfo;
}

const weekDayOrder: Weekday[] = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

/** Roles that sign in with email; others (e.g. Chef, Waitstaff, Cashier) use PIN only and don't need "Pending Setup" */
const ROLES_REQUIRING_EMAIL = new Set<string>(['admin', 'manager', 'third_party_tester']);

const getWeekStartDate = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sunday
  const diff = (day + 6) % 7;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - diff);
  return d;
};

// Format date for `<input type="date">` using local calendar date (avoids UTC off-by-one issues)
const formatDateInput = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Compute scheduled hours from start/end time strings (e.g. "09:00", "17:00" -> 8) */
const scheduledHoursFromShift = (start: string, end: string): number => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
};

/** Resolve staff for a timesheet entry; use staffFirestoreId when present so the correct person shows when staffId is duplicated */
function getStaffForTimesheetEntry(entry: TimesheetEntry, staffMembers: StaffMember[]): StaffMember | undefined {
  if (entry.staffFirestoreId) {
    const byKey = staffMembers.find((m) => (m as { firestoreId?: string }).firestoreId === entry.staffFirestoreId);
    if (byKey) return byKey;
  }
  return staffMembers.find((s) => s.id === entry.staffId);
}

/** Resolve staff for a roster shift; use staffFirestoreId when present so the correct person shows when staffId is duplicated */
function getStaffForShift(shift: Shift, staffMembers: StaffMember[]): StaffMember | undefined {
  if (shift.staffFirestoreId) {
    const byKey = staffMembers.find((m) => (m as { firestoreId?: string }).firestoreId === shift.staffFirestoreId);
    if (byKey) return byKey;
  }
  if (shift.staffId == null) return undefined;
  return staffMembers.find((s) => s.id === shift.staffId);
}

/** Single row in the payslips tab (one per staff). */
interface PayslipRow {
  key: string;
  staffId: number;
  name: string;
  hours: number;
  taxCode: string;
  hourlyRate: number;
  annualLeaveRate: number;
  workType?: 'full_time' | 'part_time' | 'casual';
  gross?: number;
  paye?: number;
  net?: number;
  holidayPay?: number;
  status?: 'draft' | 'processed';
}

const StaffManagementPage: React.FC<StaffManagementPageProps> = ({ staffMembers, userRoles, onSave, onDelete, onSendInvitation, businessHours, currentUser, businessInfo }) => {
  const canViewPins = currentUser?.permissions?.has('view_staff_pins') ?? false;
  const canViewPayroll = currentUser?.permissions?.has('view_staff_payroll') ?? false;
  const canManageStaff = currentUser?.permissions?.has('manage_staff') ?? false;
  const isAdminUser = currentUser?.isAdmin ?? false;
  const canSetHrLock = isAdminUser || canViewPayroll;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'staff' | 'roster' | 'timesheets' | 'payslips'>('staff');
  const [weekStartDate, setWeekStartDate] = useState<Date>(() => getWeekStartDate(new Date()));
  const [roster, setRoster] = useState<WeeklyRoster | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterDirty, setRosterDirty] = useState(false);
  const [rosterBulkAssignStaffKey, setRosterBulkAssignStaffKey] = useState<string>('');
  const [rosterCopyDayFrom, setRosterCopyDayFrom] = useState<Weekday | ''>('');
  const [rosterCopyDayTo, setRosterCopyDayTo] = useState<Weekday | ''>('');
  // Add Shift modal (roster)
  const [addShiftModalOpen, setAddShiftModalOpen] = useState(false);
  const [addShiftStep, setAddShiftStep] = useState<'create' | 'summary'>('create');
  const [addShiftStaffId, setAddShiftStaffId] = useState<number | null>(null);
  const [addShiftStaffFirestoreId, setAddShiftStaffFirestoreId] = useState<string | null>(null);
  const [addShiftStaffName, setAddShiftStaffName] = useState('');
  const [addShiftDay, setAddShiftDay] = useState<Weekday | null>(null);
  const [editingShiftId, setEditingShiftId] = useState<string | null>(null);
  const [addShiftStart, setAddShiftStart] = useState('09:00');
  const [addShiftEnd, setAddShiftEnd] = useState('17:00');
  const [addShiftStatus, setAddShiftStatus] = useState<ShiftStatus>('scheduled');
  const [addShiftNotes, setAddShiftNotes] = useState('');
  const [addShiftMealBreakStart, setAddShiftMealBreakStart] = useState('12:00');
  const [addShiftUnpaidMealMinutes, setAddShiftUnpaidMealMinutes] = useState(DEFAULT_UNPAID_MEAL_MINUTES);
  // Timesheets
  const [tsStartDate, setTsStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [tsEndDate, setTsEndDate] = useState<Date>(() => new Date());
  const [tsStaffFilter, setTsStaffFilter] = useState<number | ''>('');
  const [manualEntries, setManualEntries] = useState<TimesheetEntry[]>([]);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsShowAddModal, setTsShowAddModal] = useState(false);
  /** Selected staff for add-timesheet modal: use firestoreId so dropdown is unique (avoids duplicate id making selection jump to wrong name) */
  const [tsAddStaffKey, setTsAddStaffKey] = useState<string>('');
  const [tsAddDate, setTsAddDate] = useState(formatDateInput(new Date()));
  const [tsAddStart, setTsAddStart] = useState('09:00');
  const [tsAddEnd, setTsAddEnd] = useState('17:00');
  const [tsAddActualHours, setTsAddActualHours] = useState<string>('');
  const [tsAddUnpaidBreakMinutes, setTsAddUnpaidBreakMinutes] = useState<string>('');
  const [tsAddNotes, setTsAddNotes] = useState('');
  const [tsAddSaving, setTsAddSaving] = useState(false);
  const [tsImportFile, setTsImportFile] = useState<File | null>(null);
  const [tsImportLoading, setTsImportLoading] = useState(false);
  const [tsImportResult, setTsImportResult] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);

  const [staffImportFile, setStaffImportFile] = useState<File | null>(null);
  const [staffImportLoading, setStaffImportLoading] = useState(false);
  const [staffImportResult, setStaffImportResult] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);
  const rosterJsonFileInputRef = useRef<HTMLInputElement>(null);
  const [rosterJsonImporting, setRosterJsonImporting] = useState(false);

  // Payslips tab
  const [payPeriodStart, setPayPeriodStart] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [payPeriodEnd, setPayPeriodEnd] = useState<Date>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [payslipRows, setPayslipRows] = useState<PayslipRow[]>([]);
  const [payslipFillLoading, setPayslipFillLoading] = useState(false);
  const payslipLogoDataUrlRef = useRef<string | null>(null);
  const payslipLogoAspectRatioRef = useRef<number | null>(null);
  const [payslipPayFrequency, setPayslipPayFrequency] = useState<PayFrequency>('fortnightly');

  const loadPayslipLogoDataUrl = useCallback(async (): Promise<string | undefined> => {
    // Prefer email logo URL, fall back to /logo.png
    const src = businessInfo?.emailLogoUrl || '/logo.png';
    if (!src) return undefined;
    if (payslipLogoDataUrlRef.current) return payslipLogoDataUrlRef.current || undefined;
    try {
      const res = await fetch(src);
      if (!res.ok) return undefined;
      const blob = await res.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') resolve(reader.result);
          else reject(new Error('Failed to read logo data'));
        };
        reader.onerror = () => reject(new Error('Failed to load logo'));
        reader.readAsDataURL(blob);
      });
      payslipLogoDataUrlRef.current = dataUrl;

      // Also measure the logo's natural aspect ratio so we can render it without distortion in the PDF
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width && img.height) {
            payslipLogoAspectRatioRef.current = img.width / img.height;
          }
          resolve();
        };
        img.onerror = () => resolve();
        img.src = dataUrl;
      });

      return dataUrl;
    } catch {
      return undefined;
    }
  }, [businessInfo]);

  const handleOpenModal = (member: StaffMember | null = null) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
  };

  const handleSave = async (member: StaffMember) => {
    await onSave(member);
    handleCloseModal();
  };

  const handleDelete = (member: StaffMember) => {
    const firestoreId = (member as any).firestoreId;
    const memberId = firestoreId || member.id;
    
    // Build confirmation message with staff details
    const details = [
      `Name: ${member.name}`,
      member.email ? `Email: ${member.email}` : 'Email: No email',
      `Role: ${member.role}`,
      `PIN: ${member.pin || 'Not set'}`,
      member.isAdmin ? 'Administrator: Yes' : 'Administrator: No',
    ].join('\n');
    
    const confirmMessage = `Are you sure you want to delete this staff member?\n\n${details}\n\nThis action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      onDelete(memberId as number);
    }
  };

  const handleSendInvitation = async (email: string) => {
    if (!onSendInvitation) return;
    
    setSendingInvite(email);
    try {
      await onSendInvitation(email);
      alert('Invitation email sent successfully!');
    } catch (error) {
      console.error('Failed to send invitation:', error);
      alert('Failed to send invitation email. Please try again later.');
    } finally {
      setSendingInvite(null);
    }
  };

  const defaultBusinessHours = businessHours || {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Pacific/Auckland',
    days: weekDayOrder.map((day) => ({
      day,
      open: day === 'sunday' ? null : '09:00',
      close: day === 'sunday' ? null : '17:00',
      closed: day === 'sunday',
    })),
  };

  const weekStartMs = weekStartDate.getTime();
  const weekStartMsRef = useRef(weekStartMs);
  weekStartMsRef.current = weekStartMs;
  /** Shift ids last loaded from server or last successful save — used to merge concurrent edits without dropping others' shifts. */
  const rosterBaselineIdsRef = useRef<Set<string>>(new Set());
  const rosterCacheRef = useRef<Map<number, WeeklyRoster>>(new Map());

  const rosterWeekEndDate = useMemo(() => {
    const d = new Date(weekStartDate);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStartDate]);

  const rosterWeekRangeLabel = useMemo(() => {
    const short: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const startStr = weekStartDate.toLocaleDateString('en-NZ', short);
    const endStr = rosterWeekEndDate.toLocaleDateString('en-NZ', { ...short, year: 'numeric' });
    return `${startStr} – ${endStr}`;
  }, [weekStartDate, rosterWeekEndDate]);

  const deriveDateForDay = (day: Weekday) => {
    const index = weekDayOrder.indexOf(day);
    const d = new Date(weekStartDate);
    d.setDate(d.getDate() + index);
    return d.getTime();
  };

  const windowsForDay = (day: Weekday) => {
    const config = defaultBusinessHours.days.find((row) => row.day === day);
    if (!config || config.closed) return [] as { start: string; end: string }[];
    const periods = resolveDayPeriods(config);
    return periods.map((period) => ({ start: period.open, end: period.close }));
  };

  const shiftForWindow = (
    day: Weekday,
    start: string,
    end: string,
    staffId: number | null,
    staffFirestoreId?: string
  ): Shift => {
    const wall = shiftWallMinutes(start, end);
    const overnight = isOvernightShift(start, end);
    const withMeal = !overnight && requiresUnpaidMeal(wall);
    return {
      id: newRosterShiftId({ day, staffId: staffId ?? undefined }),
      day,
      date: deriveDateForDay(day),
      start,
      end,
      staffId,
      ...(staffFirestoreId ? { staffFirestoreId } : {}),
      status: staffId == null ? 'open' : 'scheduled',
      ...(withMeal
        ? {
            mealBreakStart: suggestMealBreakStart(start, end),
            unpaidMealMinutes: DEFAULT_UNPAID_MEAL_MINUTES,
          }
        : {}),
    };
  };

  const createDefaultRoster = (): WeeklyRoster => {
    const shifts: Shift[] = [];
    defaultBusinessHours.days.forEach((config) => {
      windowsForDay(config.day).forEach((hoursWindow) => {
        shifts.push(shiftForWindow(config.day, hoursWindow.start, hoursWindow.end, null));
      });
    });
    return {
      id: `week-${weekStartMs}`,
      weekStart: weekStartMs,
      timezone: defaultBusinessHours.timezone,
      shifts,
      status: 'draft',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  };

  const currentUserLabel = currentUser?.name ?? (currentUser as { email?: string })?.email ?? 'Manager';
  const canApproveRoster = (currentUser?.isAdmin || currentUser?.role === 'manager') ?? false;

  const loadRoster = useCallback(async () => {
    const requestedWeek = weekStartMs;
    const cached = rosterCacheRef.current.get(requestedWeek);
    if (cached) {
      const { shifts: uniqueCached, repaired: cacheRepaired } = ensureUniqueShiftIds(cached.shifts);
      const cachedNext =
        cacheRepaired > 0 ? { ...cached, shifts: uniqueCached, updatedAt: Date.now() } : cached;
      setRoster(cachedNext);
      rosterCacheRef.current.set(requestedWeek, cachedNext);
      rosterBaselineIdsRef.current = new Set(uniqueCached.map((s) => s.id));
      setRosterDirty(cacheRepaired > 0);
    } else {
      setRosterLoading(true);
    }
    try {
      const existing = await rosterService.getRosterByWeek(requestedWeek);
      if (weekStartMsRef.current !== requestedWeek) return;
      if (existing) {
        const { shifts: uniqueShifts, repaired } = ensureUniqueShiftIds(existing.shifts);
        const next =
          repaired > 0
            ? { ...existing, shifts: uniqueShifts, updatedAt: Date.now() }
            : existing;
        setRoster(next);
        rosterCacheRef.current.set(requestedWeek, next);
        rosterBaselineIdsRef.current = new Set(uniqueShifts.map((s) => s.id));
        setRosterDirty(repaired > 0);
        if (repaired > 0) {
          console.warn(`Roster week ${requestedWeek}: repaired ${repaired} duplicate shift id(s)`);
        }
      } else {
        const defaults = createDefaultRoster();
        setRoster(defaults);
        rosterCacheRef.current.set(requestedWeek, defaults);
        rosterBaselineIdsRef.current = new Set(defaults.shifts.map((s) => s.id));
        setRosterDirty(true);
      }
    } catch (error) {
      console.error('Failed to load roster:', error);
    } finally {
      if (weekStartMsRef.current === requestedWeek) {
        setRosterLoading(false);
      }
    }
  }, [weekStartMs]);

  useEffect(() => {
    if (activeTab === 'roster') {
      loadRoster();
    }
  }, [activeTab, loadRoster]);

  useEffect(() => {
    if (!addShiftModalOpen) return;
    if (isOvernightShift(addShiftStart, addShiftEnd) || !requiresUnpaidMeal(shiftWallMinutes(addShiftStart, addShiftEnd))) {
      return;
    }
    setAddShiftMealBreakStart((prev) => {
      const draft: Shift = {
        id: 'temp',
        day: 'monday',
        date: Date.now(),
        start: addShiftStart,
        end: addShiftEnd,
        staffId: 0,
        status: 'scheduled',
        mealBreakStart: prev,
        unpaidMealMinutes: addShiftUnpaidMealMinutes,
      };
      if (shiftMealValidationError(draft)) {
        return suggestMealBreakStart(addShiftStart, addShiftEnd, addShiftUnpaidMealMinutes);
      }
      return prev;
    });
  }, [addShiftModalOpen, addShiftStart, addShiftEnd, addShiftUnpaidMealMinutes]);

  /** Change roster week; confirms if there are unsaved edits and clears local roster until reload. */
  const navigateRosterWeek = (nextWeekCalendarDate: Date) => {
    if (rosterDirty) {
      if (!window.confirm('You have unsaved roster changes for this week. Leave without saving?')) {
        return;
      }
    }
    setRosterDirty(false);
    setRoster(null);
    setWeekStartDate(getWeekStartDate(nextWeekCalendarDate));
  };

  const handleWeekChange = (value: string) => {
    const newDate = value ? new Date(`${value}T12:00:00`) : new Date();
    navigateRosterWeek(newDate);
  };

  const handleAddShift = (day: Weekday, startTime?: string, endTime?: string) => {
    const first = windowsForDay(day)[0];
    const st = startTime || first?.start || '09:00';
    const en = endTime || first?.end || '17:00';
    const wall = shiftWallMinutes(st, en);
    const overnight = isOvernightShift(st, en);
    const withMeal = !overnight && requiresUnpaidMeal(wall);
    setRoster((prev) => {
      const next = prev ?? createDefaultRoster();
      const newShift: Shift = {
        id: newRosterShiftId({ day }),
        day,
        date: deriveDateForDay(day),
        start: st,
        end: en,
        staffId: null,
        status: 'open',
        ...(withMeal
          ? {
              mealBreakStart: suggestMealBreakStart(st, en),
              unpaidMealMinutes: DEFAULT_UNPAID_MEAL_MINUTES,
            }
          : {}),
      };
      setRosterDirty(true);
      return {
        ...next,
        shifts: [...next.shifts, newShift],
        updatedAt: Date.now(),
      };
    });
  };

  /** Unique key for a staff member (so shifts group correctly when numeric id is duplicated). */
  const getStaffKey = (member: StaffMember | { id: number; firestoreId?: string }) =>
    getStaffRosterKey(member);

  /** Open the Add Shift modal for a staff member and day (used for + Add and + Another shift). */
  const openAddShiftModal = (member: StaffMember, day: Weekday) => {
    const base = defaultBusinessHours.days.find((d) => d.day === day);
    if (base?.closed) return;
    setAddShiftStaffId(member.id);
    setAddShiftStaffFirestoreId((member as { firestoreId?: string }).firestoreId ?? null);
    setAddShiftStaffName(member.name);
    setAddShiftDay(day);
    const first = windowsForDay(day)[0];
    const st = first?.start || '09:00';
    const en = first?.end || '17:00';
    setAddShiftStart(st);
    setAddShiftEnd(en);
    setAddShiftUnpaidMealMinutes(DEFAULT_UNPAID_MEAL_MINUTES);
    setAddShiftMealBreakStart(suggestMealBreakStart(st, en, DEFAULT_UNPAID_MEAL_MINUTES));
    setAddShiftStatus('scheduled');
    setAddShiftNotes('');
    setEditingShiftId(null);
    setAddShiftStep('create');
    setAddShiftModalOpen(true);
  };

  /** Open the shift modal in edit mode for an existing shift. */
  const openEditShiftModal = (member: StaffMember, shift: Shift) => {
    setAddShiftStaffId(member.id);
    setAddShiftStaffFirestoreId((member as { firestoreId?: string }).firestoreId ?? null);
    setAddShiftStaffName(member.name);
    setAddShiftDay(shift.day);
    setAddShiftStart(shift.start);
    setAddShiftEnd(shift.end);
    setAddShiftUnpaidMealMinutes(shift.unpaidMealMinutes ?? DEFAULT_UNPAID_MEAL_MINUTES);
    setAddShiftMealBreakStart(
      shift.mealBreakStart ?? suggestMealBreakStart(shift.start, shift.end, shift.unpaidMealMinutes ?? DEFAULT_UNPAID_MEAL_MINUTES)
    );
    setAddShiftStatus(shift.status);
    setAddShiftNotes(shift.notes ?? '');
    setEditingShiftId(shift.id);
    setAddShiftStep('create');
    setAddShiftModalOpen(true);
  };

  const closeAddShiftModal = () => {
    setAddShiftModalOpen(false);
    setAddShiftStep('create');
    setAddShiftStaffId(null);
    setAddShiftStaffFirestoreId(null);
    setAddShiftDay(null);
    setEditingShiftId(null);
  };

  /** Add or edit one shift from the wizard's current form values; returns true if saved. */
  const submitAddShiftModal = (): boolean => {
    if (addShiftStaffId == null || addShiftDay == null || !roster) return false;
    const base = defaultBusinessHours.days.find((d) => d.day === addShiftDay);
    if (base?.closed) return false;
    const overnight = isOvernightShift(addShiftStart, addShiftEnd);
    const wall = shiftWallMinutes(addShiftStart, addShiftEnd);
    const withMeal = !overnight && requiresUnpaidMeal(wall);
    const nextShift: Shift = {
      id: editingShiftId ?? newRosterShiftId({ day: addShiftDay, staffId: addShiftStaffId }),
      day: addShiftDay,
      date: deriveDateForDay(addShiftDay),
      start: addShiftStart,
      end: addShiftEnd,
      staffId: addShiftStaffId,
      staffFirestoreId: addShiftStaffFirestoreId ?? undefined,
      status: addShiftStatus,
      notes: addShiftNotes.trim() || undefined,
      ...(withMeal
        ? {
            mealBreakStart: addShiftMealBreakStart,
            unpaidMealMinutes: addShiftUnpaidMealMinutes,
          }
        : {}),
    };
    if (withMeal) {
      const mealErr = shiftMealValidationError(nextShift);
      if (mealErr) {
        alert(mealErr);
        return false;
      }
    }
    const nextShifts = editingShiftId
      ? roster.shifts.map((shift) => (shift.id === editingShiftId ? nextShift : shift))
      : [...roster.shifts, nextShift];
    setRosterDirty(true);
    setRoster((prev) =>
      prev
        ? {
            ...prev,
            shifts: nextShifts,
            updatedAt: Date.now(),
          }
        : prev
    );
    void saveRosterShiftsToFirestore(nextShifts, roster.status ?? 'draft', true);
    closeAddShiftModal();
    return true;
  };

  const handleShiftChange = (shiftId: string, updates: Partial<Shift>) => {
    setRoster((prev) => {
      if (!prev) return prev;
      setRosterDirty(true);
      return {
        ...prev,
        shifts: prev.shifts.map((shift) => {
          if (shift.id !== shiftId) return shift;
          const merged: Shift = {
            ...shift,
            ...updates,
            date: updates.day ? deriveDateForDay(updates.day as Weekday) : shift.date,
          };
          return normalizeShiftMealFields(merged);
        }),
        updatedAt: Date.now(),
      };
    });
  };

  const handleDeleteShift = (shiftId: string) => {
    if (!roster) return;
    const nextShifts = roster.shifts.filter((shift) => shift.id !== shiftId);
    setRosterDirty(true);
    setRoster((prev) =>
      prev
        ? {
            ...prev,
            shifts: nextShifts,
            updatedAt: Date.now(),
          }
        : prev
    );
    void saveRosterShiftsToFirestore(nextShifts, roster.status ?? 'draft', true);
  };

  /** Add one shift per open day for every roster-eligible staff (where they don't have one yet). Use with care for large teams. */
  const handleAutoFill = () => {
    if (!roster) return;
    let added = 0;
    setRoster((prev) => {
      if (!prev) return prev;
      const newShifts = [...prev.shifts];
      rosterEligibleStaff.forEach((member) => {
        const staffKey = getStaffKey(member);
        const existing = rosterShiftsByStaffAndDay[staffKey];
        weekDayOrder.forEach((day) => {
          const businessConfig = defaultBusinessHours.days.find((d) => d.day === day);
          if (businessConfig?.closed) return;
          const hasShift = (existing?.[day]?.length ?? 0) > 0;
          if (hasShift) return;
          const windows = windowsForDay(day);
          const firestoreId = (member as { firestoreId?: string }).firestoreId;
          if (windows.length === 0 && businessConfig && !businessConfig.closed) {
            windows.push({ start: '09:00', end: '17:00' });
          }
          windows.forEach((hoursWindow) => {
            newShifts.push(shiftForWindow(day, hoursWindow.start, hoursWindow.end, member.id, firestoreId));
            added++;
          });
        });
      });
      setRosterDirty(true);
      return { ...prev, shifts: newShifts, updatedAt: Date.now() };
    });
    if (added > 0) alert(`Added ${added} shift(s) across roster staff and days. Review and save.`);
  };

  const saveRosterShiftsToFirestore = useCallback(
    async (shiftsSource: Shift[], statusForSave: RosterStatus | undefined, clearDirty: boolean): Promise<boolean> => {
      if (!roster) return false;
      if (roster.weekStart !== weekStartMs) {
        alert('This roster is still loading or the selected week changed. Please wait and try again.');
        return false;
      }
      const shiftsReady = ensureMealStartsSuggested(
        ensureUniqueShiftIds(shiftsSource).shifts
      );
      const err = firstRosterMealValidationError(shiftsReady);
      if (err) {
        alert(err);
        return false;
      }
      let fresh: WeeklyRoster | null = null;
      try {
        fresh = await rosterService.getRosterByWeek(roster.weekStart);
      } catch (e) {
        console.error(e);
        alert('Could not load the latest roster from the server. Check your connection and try again.');
        return false;
      }
      const { merged, externalAdded } = mergeExternalRosterShifts(
        shiftsReady,
        fresh?.shifts,
        rosterBaselineIdsRef.current
      );
      const postMergeErr = firstRosterMealValidationError(merged);
      if (postMergeErr) {
        alert(postMergeErr);
        return false;
      }
      try {
        const statusOut = statusForSave ?? roster.status ?? 'draft';
        await rosterService.saveRoster({
          ...roster,
          shifts: merged,
          status: statusOut,
          updatedAt: Date.now(),
        });
        rosterCacheRef.current.set(roster.weekStart, {
          ...roster,
          shifts: merged,
          status: statusOut,
          updatedAt: Date.now(),
        });
        rosterBaselineIdsRef.current = new Set(merged.map((s) => s.id));
        if (externalAdded.length > 0) {
          alert(
            `${externalAdded.length} shift(s) were added elsewhere (another tab or device). They were merged into this save so they were not removed. Please review the week.`
          );
        }
        if (clearDirty) setRosterDirty(false);
        setRoster((r) =>
          r
            ? {
                ...r,
                shifts: merged,
                status: statusOut,
                updatedAt: Date.now(),
              }
            : r
        );
        return true;
      } catch (error) {
        console.error('Failed to save roster:', error);
        alert('Failed to save roster. Please try again.');
        return false;
      }
    },
    [roster, weekStartMs]
  );

  const handleSaveRoster = async () => {
    if (!roster) return;
    const ok = await saveRosterShiftsToFirestore(roster.shifts, roster.status ?? 'draft', true);
    if (ok) alert('Roster saved successfully.');
  };

  const buildRosterExportInput = () => {
    const staffRows = rosterEligibleStaff.map((member) => {
      const staffKey = getStaffKey(member);
      const roleName = resolveStaffRoleName(member, userRoles);
      const category = rosterRoleCategory(member.role, roleName);
      return {
        name: member.name,
        roleCategory: category,
        roleLabel: category === 'Other' ? roleName : category,
        shiftsByDay: rosterShiftsByStaffAndDay[staffKey] ?? {},
      };
    });
    return {
      weekLabel: rosterWeekRangeLabel,
      weekStartMs,
      status: roster?.status ?? 'draft',
      dayLabels: rosterDayLabels(weekStartMs),
      staffRows,
    };
  };

  const handleExportRosterExcel = () => {
    if (!roster) return;
    exportRosterExcel(buildRosterExportInput());
  };

  const handleExportRosterPdf = () => {
    if (!roster) return;
    exportRosterPdf(buildRosterExportInput());
  };

  const handleExportRosterJson = () => {
    if (!roster) return;
    const doc = buildRosterJsonDocument(roster, {
      weekLabel: rosterWeekRangeLabel,
      staffMembers,
    });
    downloadRosterJson(doc);
  };

  const handleImportRosterJsonClick = () => {
    rosterJsonFileInputRef.current?.click();
  };

  const handleImportRosterJsonFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !roster) return;
    const confirmMsg =
      `Replace the roster for ${rosterWeekRangeLabel} with this JSON file?\n\n` +
      `Shifts will be applied to the week currently open (not the week stored in the file if different). ` +
      `Status becomes draft. Nothing is saved until you click Save Roster.`;
    if (!window.confirm(confirmMsg)) return;
    setRosterJsonImporting(true);
    try {
      const parsed = await readRosterJsonFile(file);
      const result = applyRosterJsonToWeek(parsed, {
        targetWeekStartMs: weekStartMs,
        timezone: roster.timezone || defaultBusinessHours.timezone,
        staffMembers,
      });
      if (!result.ok) {
        alert(result.error);
        return;
      }
      setRoster(result.roster);
      rosterCacheRef.current.set(weekStartMs, result.roster);
      rosterBaselineIdsRef.current = new Set(result.roster.shifts.map((s) => s.id));
      setRosterDirty(true);
      const warn =
        result.warnings.length > 0
          ? `\n\nNotes:\n- ${result.warnings.slice(0, 8).join('\n- ')}${result.warnings.length > 8 ? `\n- …and ${result.warnings.length - 8} more` : ''}`
          : '';
      alert(`Imported ${result.shiftCount} shift(s) into ${rosterWeekRangeLabel}. Review and Save Roster.${warn}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to import roster JSON.');
    } finally {
      setRosterJsonImporting(false);
    }
  };

  const handleSubmitRoster = async () => {
    if (!roster) return;
    const shiftsReady = ensureMealStartsSuggested(roster.shifts);
    const mealErr = firstRosterMealValidationError(shiftsReady);
    if (mealErr) {
      alert(mealErr);
      return;
    }
    const mealPersistNeeded = shiftsReady.some((s) => {
      const p = roster.shifts.find((x) => x.id === s.id);
      if (!p) return true;
      return p.mealBreakStart !== s.mealBreakStart || p.unpaidMealMinutes !== s.unpaidMealMinutes;
    });
    const mustSave = rosterDirty || mealPersistNeeded;
    try {
      if (mustSave) {
        const ok = await saveRosterShiftsToFirestore(shiftsReady, 'draft', true);
        if (!ok) return;
      }
      await rosterService.submitRoster(weekStartMs, currentUserLabel);
      setRoster((r) => (r ? { ...r, status: 'submitted', submittedAt: Date.now(), submittedBy: currentUserLabel } : r));
      alert('Roster submitted for approval.');
    } catch (error) {
      console.error('Failed to submit roster:', error);
      alert('Failed to submit roster. Please try again.');
    }
  };

  const handleApproveRoster = async () => {
    if (!roster) return;
    try {
      await rosterService.approveRoster(weekStartMs, currentUserLabel);
      setRoster((r) => r ? { ...r, status: 'approved', approvedAt: Date.now(), approvedBy: currentUserLabel } : r);
      alert('Roster approved.');
    } catch (error) {
      console.error('Failed to approve roster:', error);
      alert('Failed to approve roster. Please try again.');
    }
  };

  const handleReopenRoster = async () => {
    if (!roster) return;
    if (!window.confirm('Reopen this roster to draft? It can be edited and submitted again.')) return;
    try {
      await rosterService.reopenRoster(weekStartMs);
      setRoster((r) => r ? { ...r, status: 'draft' } : r);
      setRosterDirty(true);
      alert('Roster reopened for editing.');
    } catch (error) {
      console.error('Failed to reopen roster:', error);
      alert('Failed to reopen roster. Please try again.');
    }
  };

  /** Add one shift per open day for the selected staff (for days they don't have a shift yet). */
  const handleFillStaffWeek = (staffKey: string | '') => {
    if (staffKey === '' || !roster) return;
    const member = rosterEligibleStaff.find((m) => getStaffKey(m) === staffKey);
    if (!member) return;
    const shiftsForStaff = rosterShiftsByStaffAndDay[staffKey];
    if (!shiftsForStaff) return;
    let added = 0;
    const firestoreId = (member as { firestoreId?: string }).firestoreId;
    setRoster((prev) => {
      if (!prev) return prev;
      const newShifts: Shift[] = [...prev.shifts];
      weekDayOrder.forEach((day) => {
        const businessConfig = defaultBusinessHours.days.find((d) => d.day === day);
        if (businessConfig?.closed) return;
        const existing = shiftsForStaff[day]?.length ?? 0;
        if (existing > 0) return;
        const windows = windowsForDay(day);
        if (windows.length === 0) windows.push({ start: '09:00', end: '17:00' });
        windows.forEach((hoursWindow) => {
          newShifts.push(shiftForWindow(day, hoursWindow.start, hoursWindow.end, member.id, firestoreId));
          added++;
        });
      });
      setRosterDirty(true);
      return { ...prev, shifts: newShifts, updatedAt: Date.now() };
    });
    if (added > 0) alert(`Added ${added} shift(s) for this week. Review and save.`);
  };

  /** Set all shifts in the week to status confirmed */
  const handleConfirmAllShifts = () => {
    setRoster((prev) => {
      if (!prev) return prev;
      setRosterDirty(true);
      return {
        ...prev,
        shifts: prev.shifts.map((s) => ({ ...s, status: 'confirmed' as const })),
        updatedAt: Date.now(),
      };
    });
  };

  /** Copy all shifts from one day to another (same week). Replaces target day shifts. */
  const handleCopyDay = (fromDay: Weekday, toDay: Weekday) => {
    if (fromDay === toDay || !roster) return;
    const sourceShifts = roster.shifts.filter((s) => s.day === fromDay);
    if (sourceShifts.length === 0) {
      alert(`No shifts on ${fromDay} to copy.`);
      return;
    }
    const targetDateTs = deriveDateForDay(toDay);
    const newShiftsForDay = sourceShifts.map((s) => ({
      ...s,
      id: newRosterShiftId({ day: toDay, staffId: s.staffId }),
      day: toDay,
      date: targetDateTs,
      actualHours: undefined,
    }));
    setRoster((prev) => {
      if (!prev) return prev;
      setRosterDirty(true);
      return {
        ...prev,
        shifts: [...prev.shifts.filter((s) => s.day !== toDay), ...newShiftsForDay],
        updatedAt: Date.now(),
      };
    });
    alert(`Copied ${sourceShifts.length} shift(s) from ${fromDay} to ${toDay}. Review and save.`);
  };

  const handleCopyFromPreviousWeek = async () => {
    const prevWeekStart = new Date(weekStartDate);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const prevMs = getWeekStartDate(prevWeekStart).getTime();
    try {
      const prevRoster = await rosterService.getRosterByWeek(prevMs);
      if (!prevRoster || prevRoster.shifts.length === 0) {
        alert('No roster found for the previous week to copy.');
        return;
      }
      const newShifts: Shift[] = prevRoster.shifts.map((s) => {
        const dayIndex = weekDayOrder.indexOf(s.day);
        const newDate = new Date(weekStartDate);
        newDate.setDate(newDate.getDate() + dayIndex);
        const dateTs = newDate.getTime();
        return {
          ...s,
          // Must be unique per shift — day+timestamp alone collides for everyone on the same day.
          id: newRosterShiftId({ day: s.day, staffId: s.staffId }),
          date: dateTs,
          actualHours: undefined,
        };
      });
      const { shifts: uniqueShifts } = ensureUniqueShiftIds(newShifts);
      setRoster({
        id: `week-${weekStartMs}`,
        weekStart: weekStartMs,
        timezone: prevRoster.timezone,
        shifts: uniqueShifts,
        status: 'draft',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
      rosterBaselineIdsRef.current = new Set(uniqueShifts.map((s) => s.id));
      setRosterDirty(true);
      alert('Roster copied from previous week. Review and save.');
    } catch (error) {
      console.error('Failed to copy roster:', error);
      alert('Failed to copy from previous week.');
    }
  };

  const staffOptions = useMemo(() => {
    return staffMembers.map((member) => ({
      label: member.name,
      value: member.id,
      role: member.role,
      key: (member as { firestoreId?: string }).firestoreId ?? `staff-${member.id}`,
    }));
  }, [staffMembers]);

  /** Staff shown in the roster grid: exclude admin, third_party_tester, and anyone with includeInRoster === false */
  const rosterEligibleStaff = useMemo(() => {
    return staffMembers.filter(isRosterEligibleStaff);
  }, [staffMembers]);

  const rosterStaffByRole = useMemo(
    () => groupStaffByRosterRole(rosterEligibleStaff, userRoles),
    [rosterEligibleStaff, userRoles]
  );

  /** Shifts grouped by staff key (firestoreId or staff-${id}) then day, so each staff row only shows their shifts. */
  const rosterShiftsByStaffAndDay = useMemo(() => {
    const map: Record<string, Record<Weekday, Shift[]>> = {};
    const ensureKey = (key: string) => {
      if (!map[key]) map[key] = weekDayOrder.reduce((acc, d) => ({ ...acc, [d]: [] }), {} as Record<Weekday, Shift[]>);
    };
    rosterEligibleStaff.forEach((m) => ensureKey(getStaffKey(m)));
    if (roster) {
      roster.shifts.forEach((shift) => {
        if (shift.staffId == null) return;
        const key = shift.staffFirestoreId ?? `staff-${shift.staffId}`;
        ensureKey(key);
        if (map[key][shift.day]) {
          map[key][shift.day].push(shift);
        }
      });
      Object.values(map).forEach((byDay) => {
        weekDayOrder.forEach((day) => {
          byDay[day].sort((a, b) => a.start.localeCompare(b.start));
        });
      });
    }
    return map;
  }, [roster, rosterEligibleStaff]);

  const rosterStaffOptions = useMemo(() => {
    return rosterEligibleStaff.map((member) => ({
      label: member.name,
      value: getStaffKey(member),
      key: getStaffKey(member),
    }));
  }, [rosterEligibleStaff]);

  /** Per-staff scheduled and actual hours for the current roster week */
  const rosterHoursSummary = useMemo(() => {
    if (!roster) return [];
    const staffById = new Map(staffMembers.map((m) => [m.id, m]));
    const byStaff: Record<number, { scheduled: number; actual: number }> = {};
    roster.shifts.forEach((shift) => {
      const scheduled = paidHoursRounded(shift);
      const actual = shift.actualHours ?? scheduled;
      if (shift.staffId != null) {
        if (!byStaff[shift.staffId]) byStaff[shift.staffId] = { scheduled: 0, actual: 0 };
        byStaff[shift.staffId].scheduled += scheduled;
        byStaff[shift.staffId].actual += actual;
      }
    });
    return Array.from(staffById.values())
      .filter((m) => byStaff[m.id] && m.includeInRoster !== false)
      .map((m) => ({ staffId: m.id, name: m.name, rosterKey: (m as { firestoreId?: string }).firestoreId ?? `staff-${m.id}`, ...byStaff[m.id] }))
      .sort((a, b) => b.actual - a.actual);
  }, [roster, staffMembers]);

  const rosterTableTotalsByDay = useMemo(() => {
    if (!roster) {
      return weekDayOrder.reduce(
        (acc, day) => ({ ...acc, [day]: { hours: 0, cost: 0 } }),
        {} as Record<Weekday, { hours: number; cost: number }>
      );
    }
    return computeRosterCostByWeekday(roster.shifts, staffMembers, weekDayOrder);
  }, [roster, staffMembers]);

  // ----- Timesheets -----
  const tsStartMs = useMemo(() => {
    const d = new Date(tsStartDate);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [tsStartDate]);
  const tsEndMs = useMemo(() => {
    const d = new Date(tsEndDate);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }, [tsEndDate]);

  const loadTimesheetEntries = useCallback(async () => {
    setTsLoading(true);
    try {
      const staffId = tsStaffFilter === '' ? undefined : tsStaffFilter;
      const entries = await timesheetService.getByDateRange(tsStartMs, tsEndMs, staffId);
      setManualEntries(entries);
    } catch (error) {
      console.error('Failed to load timesheet entries:', error);
    } finally {
      setTsLoading(false);
    }
  }, [tsStartMs, tsEndMs, tsStaffFilter]);

  useEffect(() => {
    if (activeTab === 'timesheets') {
      loadTimesheetEntries();
    }
  }, [activeTab, loadTimesheetEntries]);

  // ----- Payslips: sync rows from staff when tab, staff list, or pay period changes -----
  // Always show staff list; optionally merge in saved payslips for this period if load succeeds.
  useEffect(() => {
    if (activeTab !== 'payslips') return;

    const rosterStaff = staffMembers.filter((m) => m.includeInRoster !== false);

    let cancelled = false;
    (async () => {
      let byStaffId = new Map<number, PayslipRecord>();
      try {
        const existingPayslips = await payslipService.getByPeriod(
          formatDateInput(payPeriodStart),
          formatDateInput(payPeriodEnd)
        );
        existingPayslips.forEach((p) => {
          byStaffId.set(p.staffId, p);
        });
      } catch (e) {
        console.error('Failed to load payslips for period:', e);
        // Continue: still show staff list with defaults / current state
      }

      if (cancelled) return;
      setPayslipRows((prev) => {
        const byKey = new Map(prev.map((r) => [r.key, r]));
        return rosterStaff.map((m) => {
          const key = (m as { firestoreId?: string }).firestoreId ?? `staff-${m.id}`;
          const ex = byKey.get(key);
          const persisted = byStaffId.get(m.id);
          // Holiday pay only for Casual (8%). Full time & Part time get annual leave, not pay-as-you-go.
          const isCasual = m.workType === 'casual';
          return {
            key,
            staffId: m.id,
            name: m.name,
            hours: ex?.hours ?? persisted?.hours ?? 0,
            taxCode: ex?.taxCode ?? persisted?.taxCode ?? m.taxCode ?? 'M',
            hourlyRate: ex?.hourlyRate ?? persisted?.hourlyRate ?? m.hourlyRate ?? 0,
            annualLeaveRate: isCasual
              ? (ex?.annualLeaveRate ?? persisted?.annualLeaveRate ?? (m.annualLeaveRate != null ? m.annualLeaveRate : 0.08))
              : 0,
            workType: m.workType ?? persisted?.workType,
            gross: ex?.gross ?? persisted?.gross,
            paye: ex?.paye ?? persisted?.paye,
            net: ex?.net ?? persisted?.net,
            holidayPay: ex?.holidayPay ?? persisted?.holidayPay,
            status: ex?.status ?? persisted?.status ?? 'draft',
          };
        });
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, staffMembers, payPeriodStart, payPeriodEnd]);

  const setPayslipRow = useCallback((key: string, updater: (row: PayslipRow) => PayslipRow) => {
    setPayslipRows((prev) => prev.map((r) => (r.key === key ? updater(r) : r)));
  }, []);

  const persistPayslipIfProcessed = useCallback(
    async (row: PayslipRow) => {
      if (row.status !== 'processed') return;
      if (row.gross == null || row.paye == null || row.net == null) return;

      try {
        await payslipService.upsert({
          staffId: row.staffId,
          staffName: row.name,
          payPeriodStart: formatDateInput(payPeriodStart),
          payPeriodEnd: formatDateInput(payPeriodEnd),
          hours: row.hours,
          hourlyRate: row.hourlyRate,
          annualLeaveRate: row.annualLeaveRate,
          holidayPay: row.holidayPay,
          gross: row.gross,
          paye: row.paye,
          net: row.net,
          taxCode: row.taxCode,
          workType: row.workType,
          status: row.status ?? 'processed',
        });
      } catch (e) {
        console.error('Failed to persist payslip record:', e);
      }
    },
    [payPeriodStart, payPeriodEnd]
  );

  const handlePayslipCalculate = useCallback(() => {
    // PAYE should follow IRD's pay frequency (weekly/fortnightly/monthly), not the exact number of days
    const payFrequency = payslipPayFrequency;

    setPayslipRows((prev) =>
      prev.map((row) => {
        const hours = row.hours;
        const rate = row.hourlyRate;
        const ordinaryPay = hours * rate;
        const leaveRate = row.annualLeaveRate ?? 0.08;
        const holidayPay = Math.round(ordinaryPay * leaveRate * 100) / 100;
        const gross = Math.round((ordinaryPay + holidayPay) * 100) / 100;
        const paye = gross > 0 && row.taxCode ? calculatePaye(gross, row.taxCode, payFrequency) : 0;
        const net = Math.round((gross - paye) * 100) / 100;
        return { ...row, holidayPay, gross, paye, net };
      })
    );
  }, [payPeriodStart, payPeriodEnd, payslipPayFrequency]);

  const handlePayslipFillFromTimesheet = useCallback(async () => {
    setPayslipFillLoading(true);
    try {
      const payStartMs = new Date(payPeriodStart).setHours(0, 0, 0, 0);
      const payEndMs = new Date(payPeriodEnd).setHours(23, 59, 59, 999);
      const dateStrStart = formatDateInput(payPeriodStart);
      const weekStartTs = getWeekStartTimestamp(dateStrStart);
      const manualEntries = await timesheetService.getByDateRange(payStartMs, payEndMs);
      const manualForReport: TimesheetEntryForReport[] = manualEntries.map((e) => ({
        staffId: e.staffId,
        startTime: e.startTime,
        endTime: e.endTime,
        actualHours: e.actualHours,
        date: e.date,
      }));

      const rosterShifts: RosterShiftForReport[] = [];
      for (let w = weekStartTs; w <= payEndMs; w += 7 * 24 * 60 * 60 * 1000) {
        const roster = await rosterService.getRosterByWeek(w);
        if (!roster) continue;
        roster.shifts.forEach((s) => {
          if (s.date >= payStartMs && s.date <= payEndMs && s.staffId != null) {
            rosterShifts.push({
              staffId: s.staffId,
              start: s.start,
              end: s.end,
              actualHours: s.actualHours,
              date: s.date,
              mealBreakStart: s.mealBreakStart,
              unpaidMealMinutes: s.unpaidMealMinutes,
            });
          }
        });
      }

      const staffMap = new Map(staffMembers.map((m) => [m.id, m.name]));
      const summary = getTimesheetLaborSummary(rosterShifts, manualForReport, staffMap);

      setPayslipRows((prev) =>
        prev.map((row) => {
          const found = summary.byStaff.find((s) => s.staffId === row.staffId);
          const hours = found?.hours ?? 0;
          return { ...row, hours };
        })
      );
    } catch (e) {
      console.error('Fill from timesheet failed:', e);
    } finally {
      setPayslipFillLoading(false);
    }
  }, [payPeriodStart, payPeriodEnd, staffMembers]);

  const [rosterDerivedRows, setRosterDerivedRows] = useState<{
    staffId: number;
    staffKey: string;
    staffName: string;
    date: number;
    start: string;
    end: string;
    /** Wall time from scheduled start–end (on site). */
    onSiteHours: number;
    actualHours: number;
    notes?: string;
    shiftId: string;
    weekStart: number;
  }[]>([]);
  useEffect(() => {
    if (activeTab !== 'timesheets') return;
    let cancelled = false;
    (async () => {
      const startWeek = getWeekStartDate(new Date(tsStartMs));
      const endWeek = getWeekStartDate(new Date(tsEndMs));
      const weeks: number[] = [];
      for (let w = startWeek.getTime(); w <= endWeek.getTime(); w += 7 * 24 * 60 * 60 * 1000) weeks.push(w);
      const rows: {
        staffId: number;
        staffKey: string;
        staffName: string;
        date: number;
        start: string;
        end: string;
        onSiteHours: number;
        actualHours: number;
        notes?: string;
        shiftId: string;
        weekStart: number;
      }[] = [];
      for (const weekStart of weeks) {
        if (cancelled) return;
        const r = await rosterService.getRosterByWeek(weekStart);
        if (!r) continue;
        r.shifts.forEach((shift) => {
          if (shift.date < tsStartMs || shift.date > tsEndMs) return;
          if (shift.staffId == null) return;
          const staff = getStaffForShift(shift, staffMembers);
          if (!staff) return;
          if (tsStaffFilter !== '' && staff.id !== tsStaffFilter) return;
          const actual = shift.actualHours ?? paidHoursRounded(shift);
          const staffKey = (staff as { firestoreId?: string }).firestoreId ?? `staff-${staff.id}`;
          rows.push({
            staffId: staff.id,
            staffKey,
            staffName: staff.name,
            date: shift.date,
            start: shift.start,
            end: shift.end,
            onSiteHours: wallHoursRounded(shift.start, shift.end),
            actualHours: actual,
            notes: shift.notes,
            shiftId: shift.id,
            weekStart,
          });
        });
      }
      if (!cancelled) setRosterDerivedRows(rows);
    })();
    return () => { cancelled = true; };
  }, [activeTab, tsStartMs, tsEndMs, tsStaffFilter, staffMembers]);

  const updateRosterShiftActualHours = useCallback(
    async (shiftId: string, weekStart: number, actualHours: number) => {
      try {
        const r = await rosterService.getRosterByWeek(weekStart);
        if (!r) return;
        let shifts = r.shifts;
        if (roster && roster.weekStart === weekStart && rosterDirty) {
          const { merged, externalAdded } = mergeExternalRosterShifts(
            roster.shifts,
            r.shifts,
            rosterBaselineIdsRef.current
          );
          shifts = merged;
          if (externalAdded.length > 0) {
            alert(
              `${externalAdded.length} shift(s) from the server were merged while saving actual hours so they were not removed. Please review the roster for this week.`
            );
          }
        }
        const shift = shifts.find((s) => s.id === shiftId);
        if (!shift) return;
        const updatedShifts = shifts.map((s) => (s.id === shiftId ? { ...s, actualHours } : s));
        await rosterService.saveRoster({ ...r, shifts: updatedShifts });
        rosterBaselineIdsRef.current = new Set(updatedShifts.map((s) => s.id));
        if (roster && roster.weekStart === weekStart && rosterDirty) {
          setRoster((prev) =>
            prev && prev.weekStart === weekStart
              ? { ...prev, shifts: updatedShifts, updatedAt: Date.now() }
              : prev
          );
          setRosterDirty(false);
        }
        setRosterDerivedRows((prev) => prev.map((row) => (row.shiftId === shiftId ? { ...row, actualHours } : row)));
      } catch (e) {
        console.error('Failed to update shift actual hours:', e);
        alert('Failed to save actual hours.');
      }
    },
    [roster, rosterDirty]
  );

  const timesheetTotalsByStaff = useMemo(() => {
    const byStaff: Record<string, { name: string; hours: number }> = {};
    rosterDerivedRows.forEach((r) => {
      const key = r.staffKey;
      if (!byStaff[key]) byStaff[key] = { name: r.staffName, hours: 0 };
      byStaff[key].hours += r.actualHours;
    });
    manualEntries.forEach((e) => {
      const hours = e.actualHours ?? scheduledHoursFromShift(e.startTime, e.endTime);
      const staff = getStaffForTimesheetEntry(e, staffMembers);
      const key = e.staffFirestoreId ?? `staff-${e.staffId}`;
      if (!byStaff[key]) byStaff[key] = { name: staff?.name ?? String(e.staffId), hours: 0 };
      byStaff[key].hours += hours;
    });
    return Object.entries(byStaff).map(([staffKey, v]) => ({ staffKey, ...v })).sort((a, b) => b.hours - a.hours);
  }, [rosterDerivedRows, manualEntries, staffMembers]);

  const handleSubmitTimesheetEntry = async () => {
    const selectedStaff = tsAddStaffKey ? staffMembers.find((m) => (m as { firestoreId?: string }).firestoreId === tsAddStaffKey || `staff-${m.id}` === tsAddStaffKey) : null;
    if (!selectedStaff) {
      alert('Please select a staff member.');
      return;
    }
    const d = new Date(tsAddDate);
    d.setHours(0, 0, 0, 0);
    const dateTs = d.getTime();
    const spanHours = scheduledHoursFromShift(tsAddStart, tsAddEnd);
    const unpaidMinutesNum = tsAddUnpaidBreakMinutes.trim() === '' ? 0 : Math.max(0, Number(tsAddUnpaidBreakMinutes));
    const spanMinusUnpaid = Math.max(0, spanHours - unpaidMinutesNum / 60);
    const actualHoursValue =
      tsAddActualHours === '' ? spanMinusUnpaid : Math.max(0, Number(tsAddActualHours));
    setTsAddSaving(true);
    try {
      const staffFirestoreId = (selectedStaff as { firestoreId?: string }).firestoreId ?? undefined;
      await timesheetService.add({
        staffId: selectedStaff.id,
        staffFirestoreId,
        date: dateTs,
        startTime: tsAddStart,
        endTime: tsAddEnd,
        actualHours: actualHoursValue,
        // Store unpaid break so we can audit entitlements later
        // (paid rest breaks remain inside the span and are not subtracted)
        unpaidBreakMinutes: unpaidMinutesNum > 0 ? unpaidMinutesNum : undefined,
        notes: tsAddNotes || undefined,
      });
      loadTimesheetEntries();
      setTsShowAddModal(false);
      setTsAddStaffKey('');
      setTsAddDate(formatDateInput(new Date()));
      setTsAddStart('09:00');
      setTsAddEnd('17:00');
      setTsAddActualHours('');
      setTsAddUnpaidBreakMinutes('');
      setTsAddNotes('');
    } catch (error) {
      console.error('Failed to add timesheet entry:', error);
      alert('Failed to add entry.');
    } finally {
      setTsAddSaving(false);
    }
  };

  const handleDeleteTimesheetEntry = async (id: string) => {
    if (!window.confirm('Delete this timesheet entry?')) return;
    try {
      await timesheetService.delete(id);
      loadTimesheetEntries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      alert('Failed to delete entry.');
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') inQuotes = !inQuotes;
      else if (c === ',' && !inQuotes) { out.push(cur.trim()); cur = ''; }
      else cur += c;
    }
    out.push(cur.trim().replace(/^"|"$/g, ''));
    return out;
  };

  const handleTimesheetImportCSV = useCallback(async () => {
    if (!tsImportFile) return;
    setTsImportLoading(true);
    setTsImportResult(null);
    const errors: string[] = [];
    let imported = 0;
    let failed = 0;
    try {
      const text = await tsImportFile.text();
      const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        setTsImportResult({ imported: 0, failed: 0, errors: ['CSV must have a header row and at least one data row.'] });
        setTsImportLoading(false);
        setTsImportFile(null);
        return;
      }
      const header = parseCSVLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, ' ').trim());
      const dateIdx = header.findIndex((h) => h === 'date');
      const staffIdx = header.findIndex((h) => h === 'staff name' || h === 'staff');
      const startIdx = header.findIndex((h) => h === 'start (24h)' || h === 'start');
      const endIdx = header.findIndex((h) => h === 'end (24h)' || h === 'end');
      const notesIdx = header.findIndex((h) => h === 'notes');
      if (dateIdx < 0 || staffIdx < 0 || startIdx < 0 || endIdx < 0) {
        setTsImportResult({ imported: 0, failed: 0, errors: ['CSV must have columns: Date, Staff name (or Staff), Start, End.'] });
        setTsImportLoading(false);
        setTsImportFile(null);
        return;
      }
      const normalizeTime = (t: string): string => {
        const s = String(t).trim();
        const match = s.match(/^(\d{1,2}):(\d{2})/);
        if (match) {
          const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
          const m = Math.min(59, Math.max(0, parseInt(match[2], 10)));
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        }
        return s || '09:00';
      };
      for (let i = 1; i < lines.length; i++) {
        const cells = parseCSVLine(lines[i]);
        const dateStr = (cells[dateIdx] || '').trim();
        const staffName = (cells[staffIdx] || '').trim();
        const startTime = normalizeTime(cells[startIdx] || '09:00');
        const endTime = normalizeTime(cells[endIdx] || '17:00');
        const notes = notesIdx >= 0 ? (cells[notesIdx] || '').trim() : '';
        if (!dateStr || !staffName) {
          failed++;
          errors.push(`Row ${i + 1}: missing date or staff name`);
          continue;
        }
        const staff = staffMembers.find((m) => m.name.trim().toLowerCase() === staffName.toLowerCase());
        if (!staff) {
          failed++;
          errors.push(`Row ${i + 1}: staff "${staffName}" not found`);
          continue;
        }
        const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/) || dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        let dateTs: number;
        if (dateMatch) {
          const d = dateMatch[1].length === 4
            ? new Date(parseInt(dateMatch[1], 10), parseInt(dateMatch[2], 10) - 1, parseInt(dateMatch[3], 10))
            : new Date(parseInt(dateMatch[3], 10), parseInt(dateMatch[2], 10) - 1, parseInt(dateMatch[1], 10));
          d.setHours(0, 0, 0, 0);
          dateTs = d.getTime();
        } else {
          failed++;
          errors.push(`Row ${i + 1}: invalid date "${dateStr}" (use YYYY-MM-DD)`);
          continue;
        }
        try {
          const staffFirestoreId = (staff as { firestoreId?: string }).firestoreId ?? undefined;
          await timesheetService.add({
            staffId: staff.id,
            staffFirestoreId,
            date: dateTs,
            startTime,
            endTime,
            notes: notes || undefined,
          });
          imported++;
        } catch (err) {
          failed++;
          errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : 'Failed to save'}`);
        }
      }
      setTsImportResult({ imported, failed, errors: errors.slice(0, 20) });
      if (imported > 0) loadTimesheetEntries();
    } catch (err) {
      setTsImportResult({ imported: 0, failed: 0, errors: [err instanceof Error ? err.message : 'Failed to read file'] });
    } finally {
      setTsImportLoading(false);
      setTsImportFile(null);
    }
  }, [tsImportFile, staffMembers]);

  const handleStaffImportCSV = useCallback(async () => {
    if (!isAdminUser || !staffImportFile) return;
    setStaffImportLoading(true);
    setStaffImportResult(null);
    const errors: string[] = [];
    let imported = 0;
    let failed = 0;
    try {
      const text = await staffImportFile.text();
      const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        setStaffImportResult({ imported: 0, failed: 0, errors: ['CSV must have a header row and at least one data row.'] });
        return;
      }
      const header = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
      const nameIdx = header.findIndex((h) => h === 'name');
      const emailIdx = header.findIndex((h) => h === 'email');
      const roleIdx = header.findIndex((h) => h === 'role');
      const pinIdx = header.findIndex((h) => h === 'pin');
      const adminIdx = header.findIndex((h) => h === 'isadmin' || h === 'is_admin');
      if (nameIdx < 0 || roleIdx < 0) {
        setStaffImportResult({ imported: 0, failed: 0, errors: ['CSV must at least have columns: Name, Role. Optional: Email, PIN, IsAdmin.'] });
        return;
      }
      for (let i = 1; i < lines.length; i++) {
        const cells = parseCSVLine(lines[i]);
        const name = (cells[nameIdx] || '').trim();
        const email = emailIdx >= 0 ? (cells[emailIdx] || '').trim() : '';
        const role = (cells[roleIdx] || '').trim();
        const pin = pinIdx >= 0 ? (cells[pinIdx] || '').trim() : '';
        const isAdmin = adminIdx >= 0 ? /^true|1|yes$/i.test((cells[adminIdx] || '').trim()) : false;
        if (!name || !role) {
          failed++;
          errors.push(`Row ${i + 1}: missing Name or Role`);
          continue;
        }
        const existing = staffMembers.find((m) => m.name.trim().toLowerCase() === name.toLowerCase() && m.role.toLowerCase() === role.toLowerCase());
        const member: StaffMember = existing
          ? {
              ...existing,
              name,
              role,
              email: email || existing.email,
              pin: pin || existing.pin,
              isAdmin,
            }
          : {
              id: 0,
              name,
              role,
              pin: pin || '',
              email: email || undefined,
              isAdmin,
            };
        try {
          await onSave(member);
          imported++;
        } catch (err) {
          failed++;
          errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : 'Failed to save staff member'}`);
        }
      }
      setStaffImportResult({ imported, failed, errors: errors.slice(0, 20) });
    } catch (err) {
      setStaffImportResult({ imported: 0, failed: 0, errors: [err instanceof Error ? err.message : 'Failed to read file'] });
    } finally {
      setStaffImportLoading(false);
      setStaffImportFile(null);
    }
  }, [isAdminUser, staffImportFile, staffMembers, onSave]);

  const tsActualHoursKeyboard = useSoftKeyboardField({
    id: 'ts-add-actual-hours',
    type: 'numeric',
    label: 'Actual hours',
    value: tsAddActualHours,
    onChange: setTsAddActualHours,
    manualValidator: (v) => v === '' || /^\d*\.?\d*$/.test(v),
  });
  const tsNotesKeyboard = useSoftKeyboardField({
    id: 'ts-add-notes',
    type: 'alphanumeric',
    label: 'Notes',
    value: tsAddNotes,
    onChange: setTsAddNotes,
  });

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800" data-testid="staff-page-heading">Staff Management</h1>
            <div className="flex items-center gap-3">
              <div className="flex bg-gray-100 rounded-lg overflow-hidden text-sm font-medium">
                <button
                  onClick={() => setActiveTab('staff')}
                  data-testid="staff-tab-list"
                  className={`px-4 py-2 ${activeTab === 'staff' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                >
                  Staff List
                </button>
                <button
                  onClick={() => setActiveTab('roster')}
                  data-testid="staff-tab-roster"
                  className={`px-4 py-2 ${activeTab === 'roster' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                >
                  Roster
                </button>
                <button
                  onClick={() => setActiveTab('timesheets')}
                  data-testid="staff-tab-timesheets"
                  className={`px-4 py-2 ${activeTab === 'timesheets' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                >
                  Timesheets
                </button>
                {canViewPayroll && (
                  <button
                    onClick={() => setActiveTab('payslips')}
                    className={`px-4 py-2 ${activeTab === 'payslips' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
                  >
                    Payslips
                  </button>
                )}
              </div>
              {activeTab === 'staff' ? (
                <div className="flex items-center gap-2">
                  {isAdminUser && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const header = ['Name', 'Email', 'Role', 'PIN', 'IsAdmin'];
                          const rows = staffMembers.map((m) => [
                            m.name,
                            m.email || '',
                            m.role,
                            m.pin || '',
                            m.isAdmin ? 'true' : 'false',
                          ]);
                          const csv = [header, ...rows]
                            .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
                            .join('\n');
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const a = document.createElement('a');
                          a.href = URL.createObjectURL(blob);
                          a.download = 'staff-export.csv';
                          a.click();
                          URL.revokeObjectURL(a.href);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                      >
                        Export staff CSV
                      </button>
                      <label className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setStaffImportFile(f || null);
                            if (f) setStaffImportResult(null);
                            e.target.value = '';
                          }}
                        />
                        Import staff CSV
                      </label>
                      {staffImportFile && (
                        <button
                          type="button"
                          onClick={handleStaffImportCSV}
                          disabled={staffImportLoading}
                          className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
                        >
                          {staffImportLoading ? 'Importing…' : `Upload ${staffImportFile.name}`}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => handleOpenModal()}
                    className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add New Staff
                  </button>
                </div>
              ) : activeTab === 'roster' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={rosterJsonFileInputRef}
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={handleImportRosterJsonFile}
                  />
                  <button
                    type="button"
                    onClick={handleExportRosterJson}
                    disabled={!roster || rosterLoading || rosterJsonImporting}
                    title="Accurate backup for re-import (preferred over Excel)"
                    className="bg-white text-gray-800 border border-gray-300 font-bold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50"
                  >
                    Export JSON
                  </button>
                  <button
                    type="button"
                    onClick={handleImportRosterJsonClick}
                    disabled={!roster || rosterLoading || rosterJsonImporting}
                    title="Restore a previously exported roster JSON into this week"
                    className="bg-white text-gray-800 border border-gray-300 font-bold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50"
                  >
                    {rosterJsonImporting ? 'Importing…' : 'Import JSON'}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportRosterExcel}
                    disabled={!roster || rosterLoading}
                    className="bg-white text-gray-800 border border-gray-300 font-bold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50"
                  >
                    Export Excel
                  </button>
                  <button
                    type="button"
                    onClick={handleExportRosterPdf}
                    disabled={!roster || rosterLoading}
                    className="bg-white text-gray-800 border border-gray-300 font-bold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50"
                  >
                    Print PDF
                  </button>
                  <button
                    onClick={handleSaveRoster}
                    disabled={!rosterDirty || rosterLoading}
                    className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 disabled:opacity-50"
                  >
                    Save Roster
                  </button>
                </div>
              ) : activeTab === 'payslips' ? null : (
                <button
                  onClick={() => setTsShowAddModal(true)}
                  className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Entry
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        {activeTab === 'staff' ? (
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            {isAdminUser && staffImportResult && (
              <div className={`m-4 p-3 rounded-lg border text-sm ${staffImportResult.failed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <p className="font-medium">
                  {staffImportResult.imported > 0 && <span className="text-green-800">Imported {staffImportResult.imported} staff record(s).</span>}
                  {staffImportResult.failed > 0 && <span className="text-amber-800"> {staffImportResult.failed} row(s) failed.</span>}
                </p>
                {staffImportResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-amber-800 text-xs">
                    {staffImportResult.errors.map((msg, idx) => <li key={idx}>{msg}</li>)}
                  </ul>
                )}
                <button type="button" onClick={() => setStaffImportResult(null)} className="mt-2 text-xs text-gray-600 underline">
                  Dismiss
                </button>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work type</th>
                  {canViewPins && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PIN</th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {staffMembers.map((member) => {
                  const role = userRoles.find(r => r.id === member.role);
                  const hasAuth = !!member.authUserId;
                  const roleRequiresEmail = ROLES_REQUIRING_EMAIL.has(member.role);
                  const isActive = hasAuth || !roleRequiresEmail; // PIN-only roles are Active once created
                  // Use firestoreId as key if available (for Firestore), otherwise use id
                  const uniqueKey = (member as any).firestoreId || `staff-${member.id}`;
                  return (
                    <tr key={uniqueKey}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{member.name}</div>
                        {member.isAdmin && (
                          <span className="text-xs text-pink-600 font-semibold">👑 Admin</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.email || 'No email'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 capitalize">
                          {role?.name || member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.workType === 'full_time' ? 'Full time' : member.workType === 'part_time' ? 'Part time' : member.workType === 'casual' ? 'Casual' : '—'}
                      </td>
                      {canViewPins && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900 font-semibold">
                            {member.pin || '—'}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isActive ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Pending Setup
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {!hasAuth && member.email && onSendInvitation && (
                          <button
                            onClick={() => handleSendInvitation(member.email!)}
                            disabled={sendingInvite === member.email}
                            className="text-blue-600 hover:text-blue-900 mr-4 disabled:opacity-50"
                            title="Send invitation email"
                          >
                            {sendingInvite === member.email ? 'Sending...' : '📧 Invite'}
                          </button>
                        )}
                        <button onClick={() => handleOpenModal(member)} className="text-pink-600 hover:text-pink-900 mr-4"><EditIcon className="w-5 h-5" /></button>
                        <button onClick={() => handleDelete(member)} className="text-red-600 hover:text-red-900"><TrashIcon className="w-5 h-5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'roster' ? (
          <div className="space-y-3">
            <div className="bg-white shadow rounded-lg p-2.5 flex flex-col gap-2">
              <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto_auto] gap-2 items-end">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Week starting</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="date"
                      value={formatDateInput(weekStartDate)}
                      onChange={(e) => handleWeekChange(e.target.value)}
                      className="px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-pink-500 focus:border-pink-500"
                    />
                    <button
                      type="button"
                      onClick={() => navigateRosterWeek(new Date())}
                      className="px-2.5 py-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                      title="Go to current week"
                    >
                      Today
                    </button>
                    <p className="text-xs text-gray-500">{rosterWeekRangeLabel}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = new Date(weekStartDate);
                      newDate.setDate(newDate.getDate() - 7);
                      navigateRosterWeek(newDate);
                    }}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-xs font-medium"
                    title="Previous week"
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = new Date(weekStartDate);
                      newDate.setDate(newDate.getDate() + 7);
                      navigateRosterWeek(newDate);
                    }}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-xs font-medium"
                    title="Next week"
                  >
                    Next →
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleAutoFill}
                    className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs font-semibold"
                    title="Auto-fill shifts from business hours"
                  >
                    Auto-fill
                  </button>
                  <button
                    onClick={handleCopyFromPreviousWeek}
                    className="px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs font-semibold"
                    title="Copy shifts from previous week"
                  >
                    Copy previous week
                  </button>
                </div>
              </div>
              <p className="text-[10px] leading-tight text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-0.5">
                Saving reloads the latest roster from the server and merges in any shifts added elsewhere so they are not dropped. If you see a message about merged shifts, review the week.
              </p>
              {roster && (
                <div className="flex flex-wrap items-center gap-1.5 py-1.5 border-t border-gray-200">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      roster.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : roster.status === 'submitted'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {(roster.status ?? 'draft').charAt(0).toUpperCase() + (roster.status ?? 'draft').slice(1)}
                  </span>
                  {roster.submittedBy && (
                    <span className="text-[11px] text-gray-500">Submitted by {roster.submittedBy}</span>
                  )}
                  {roster.approvedBy && (
                    <span className="text-[11px] text-gray-500">Approved by {roster.approvedBy}</span>
                  )}
                  <div className="flex flex-wrap gap-1 ml-1">
                    {(roster.status === 'draft' || !roster.status) && (
                      <button
                        onClick={handleSubmitRoster}
                        className="px-2.5 py-1.5 bg-amber-500 text-white rounded-md hover:bg-amber-600 text-xs font-semibold"
                      >
                        Submit for approval
                      </button>
                    )}
                    {roster.status === 'submitted' && canApproveRoster && (
                      <>
                        <button
                          onClick={handleApproveRoster}
                          className="px-2.5 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-semibold"
                        >
                          Approve
                        </button>
                        <button
                          onClick={handleReopenRoster}
                          className="px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-xs font-semibold"
                        >
                          Reopen to draft
                        </button>
                      </>
                    )}
                    {roster.status === 'approved' && canApproveRoster && (
                      <button
                        onClick={handleReopenRoster}
                        className="px-2.5 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-xs font-semibold"
                      >
                        Reopen to draft
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1 ml-auto pl-2">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase">Quick actions</span>
                    <select
                      value={rosterBulkAssignStaffKey}
                      onChange={(e) => setRosterBulkAssignStaffKey(e.target.value ?? '')}
                      className="text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white"
                    >
                      <option value="">Add staff to all days…</option>
                      {rosterStaffOptions.map((s) => (
                        <option key={s.key} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleFillStaffWeek(rosterBulkAssignStaffKey)}
                      disabled={rosterBulkAssignStaffKey === ''}
                      className="px-2 py-1 bg-slate-100 rounded-md hover:bg-slate-200 text-xs disabled:opacity-50"
                    >
                      Apply
                    </button>
                    <button
                      onClick={handleConfirmAllShifts}
                      className="px-2 py-1 bg-slate-100 rounded-md hover:bg-slate-200 text-xs"
                      title="Set all shifts to Confirmed"
                    >
                      Confirm all
                    </button>
                    <select
                      value={rosterCopyDayFrom}
                      onChange={(e) => setRosterCopyDayFrom((e.target.value || '') as Weekday | '')}
                      className="text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white"
                    >
                      <option value="">Copy from…</option>
                      {weekDayOrder.map((d) => (
                        <option key={d} value={d}>
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </option>
                      ))}
                    </select>
                    <select
                      value={rosterCopyDayTo}
                      onChange={(e) => setRosterCopyDayTo((e.target.value || '') as Weekday | '')}
                      className="text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white"
                    >
                      <option value="">…to</option>
                      {weekDayOrder.map((d) => (
                        <option key={d} value={d}>
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => rosterCopyDayFrom && rosterCopyDayTo && handleCopyDay(rosterCopyDayFrom, rosterCopyDayTo)}
                      disabled={!rosterCopyDayFrom || !rosterCopyDayTo}
                      className="px-2 py-1 bg-slate-100 rounded-md hover:bg-slate-200 text-xs disabled:opacity-50"
                    >
                      Copy day
                    </button>
                  </div>
                </div>
              )}
              {roster && rosterHoursSummary.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                  <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap">
                    <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide shrink-0">Hours summary</p>
                    {rosterHoursSummary.map(({ staffId, name, scheduled, actual, rosterKey }) => (
                      <span key={rosterKey ?? `roster-hrs-${staffId}`} className="text-xs shrink-0">
                        <span className="font-medium text-gray-900">{name}</span>
                        <span className="text-gray-500 ml-1">
                          {actual}h {actual !== scheduled ? `(sched: ${scheduled}h)` : ''}
                        </span>
                      </span>
                    ))}
                    <span className="text-xs text-gray-500 shrink-0">
                      Total: {rosterHoursSummary.reduce((a, b) => a + b.actual, 0).toFixed(1)}h
                    </span>
                  </div>
                </div>
              )}
              {rosterLoading ? (
                <p className="text-sm text-gray-500">Loading roster...</p>
              ) : !roster ? (
                <p className="text-sm text-red-500">Unable to load roster configuration.</p>
              ) : (
                <div className="text-xs text-gray-600 flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-800">{rosterWeekRangeLabel}</span>
                  <span>
                    <span className="text-gray-500">Timezone</span> {roster.timezone}
                  </span>
                  {rosterDirty && <span className="text-pink-600 font-semibold">Unsaved changes</span>}
                </div>
              )}
            </div>
            {roster && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <p className="text-sm text-gray-500 px-4 py-2 bg-gray-50 border-b border-gray-200">
                  One row per staff. Click <strong>+ Add</strong> to allocate a shift. Times here are <strong>planned</strong> only (copy to next week keeps these). Adjust actual hours for late starts or extra work in the <strong>Timesheet</strong> tab. Use <strong>Export JSON</strong> / <strong>Import JSON</strong> for accurate backup and restore (Excel/PDF are for printing only).
                </p>
                <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-gray-100 bg-white text-xs text-gray-600">
                  <span className="font-semibold text-gray-500 uppercase tracking-wide">Shift status</span>
                  <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 bg-gray-100 text-gray-700">Open</span>
                  <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 bg-blue-100 text-blue-700">Scheduled</span>
                  <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 bg-green-100 text-green-700">Confirmed</span>
                  <span className="text-gray-400">· Times use 12-hour clock, 15-minute steps</span>
                </div>
                <div className="p-4 space-y-3 bg-slate-50">
                  <div className="grid grid-cols-[220px_repeat(7,minmax(120px,1fr))_140px] gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                    <div>Employee</div>
                    {weekDayOrder.map((day) => {
                      const dateForDay = new Date(deriveDateForDay(day));
                      return (
                        <div key={`timeline-head-${day}`} className="text-center">
                          {dateForDay.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })}
                        </div>
                      );
                    })}
                    <div className="text-right">Total hours</div>
                  </div>
                  {rosterStaffByRole.map((group) => (
                    <div key={`roster-role-${group.label}`} className="space-y-2">
                      <div className="rounded-md bg-slate-200/80 border border-slate-300 px-3 py-1.5">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700">{group.label}</p>
                      </div>
                      {group.members.map((member) => {
                    const staffKey = getStaffKey(member);
                    const shiftsForStaff = rosterShiftsByStaffAndDay[staffKey];
                    const totalHours = weekDayOrder.reduce((sum, day) => sum + (shiftsForStaff?.[day] ?? []).reduce((dSum, s) => dSum + paidHoursRounded(s), 0), 0);
                    const totalCost = totalHours * (member.hourlyRate ?? 0);
                    return (
                      <div key={`timeline-row-${staffKey}`} className="grid grid-cols-[220px_repeat(7,minmax(120px,1fr))_140px] gap-2 items-stretch">
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-sm font-semibold text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{userRoles.find((r) => r.id === member.role)?.name ?? member.role}</p>
                        </div>
                        {weekDayOrder.map((day) => {
                          const shifts = shiftsForStaff?.[day] ?? [];
                          return (
                            <div key={`timeline-cell-${staffKey}-${day}`} className="rounded-lg border border-slate-200 bg-white p-2 min-h-[62px]">
                              {shifts.length === 0 ? (
                                <button
                                  type="button"
                                  onClick={() => openAddShiftModal(member, day)}
                                  className="w-full h-full text-xs text-gray-400 hover:text-pink-600"
                                >
                                  + Add shift
                                </button>
                              ) : (
                                <div className="space-y-1">
                                  {shifts.map((shift) => (
                                    <button
                                      key={`timeline-shift-${shift.id}`}
                                      type="button"
                                      onClick={() => openEditShiftModal(member, shift)}
                                      className="w-full text-left rounded-md bg-sky-50 border border-sky-200 px-2 py-1 hover:bg-sky-100"
                                      title="Edit shift"
                                    >
                                      <p className="text-xs font-medium text-sky-800">{shift.start} - {shift.end}</p>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-sky-700">{paidHoursRounded(shift)}h</span>
                                        <span className="text-[10px] text-sky-700 underline">Edit</span>
                                      </div>
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => openAddShiftModal(member, day)}
                                    className="text-[11px] text-blue-600 hover:text-blue-800"
                                  >
                                    + Another shift
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="rounded-lg bg-blue-50 border border-blue-100 text-right px-3 py-2">
                          <p className="text-sm font-semibold text-blue-900">{totalHours.toFixed(1)}h</p>
                          <p className="text-xs text-blue-800">${totalCost.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                      })}
                    </div>
                  ))}
                  <div className="grid grid-cols-[220px_repeat(7,minmax(120px,1fr))_140px] gap-2">
                    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-xs font-semibold text-gray-600">Daily total</div>
                    {weekDayOrder.map((day) => (
                      <div key={`timeline-total-${day}`} className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-sm font-semibold text-blue-900 text-center">
                        <p>{rosterTableTotalsByDay[day].hours.toFixed(1)}h</p>
                        <p className="text-xs font-medium text-blue-800">${rosterTableTotalsByDay[day].cost.toFixed(2)}</p>
                      </div>
                    ))}
                    <div className="rounded-lg bg-blue-100 border border-blue-200 px-3 py-2 text-right text-sm font-bold text-blue-950">
                      <p>{Object.values(rosterTableTotalsByDay).reduce((sum, val) => sum + val.hours, 0).toFixed(1)}h</p>
                      <p className="text-xs">${Object.values(rosterTableTotalsByDay).reduce((sum, val) => sum + val.cost, 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {addShiftModalOpen && addShiftDay && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-4 sm:p-6">
                  <div className="grid grid-cols-2 mb-5 rounded-md overflow-hidden border border-gray-200 text-sm">
                    {(['create', 'summary'] as const).map((step) => (
                      <div
                        key={`wizard-step-${step}`}
                        className={`py-2 px-3 text-center font-semibold capitalize ${addShiftStep === step ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-900'}`}
                      >
                        {step === 'create' ? 'Create shift' : 'Summary'}
                      </div>
                    ))}
                  </div>
                  {addShiftStep === 'create' && (
                    <div className="space-y-4">
                      <h3 className="text-2xl font-semibold text-gray-900">{editingShiftId ? 'Edit shift' : 'Create new shift'}</h3>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">Staff</span>
                        <p className="text-sm font-medium text-gray-900">{addShiftStaffName}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase">Day</span>
                        <p className="text-sm font-medium text-gray-900 capitalize">{addShiftDay}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <RosterQuarterHourTime label="Start" value={addShiftStart} onChange={setAddShiftStart} className="[&_select]:text-sm [&_select]:py-2 [&_select]:px-2" />
                        <RosterQuarterHourTime label="End" value={addShiftEnd} onChange={setAddShiftEnd} className="[&_select]:text-sm [&_select]:py-2 [&_select]:px-2" />
                      </div>
                      {!isOvernightShift(addShiftStart, addShiftEnd) &&
                        requiresUnpaidMeal(shiftWallMinutes(addShiftStart, addShiftEnd)) && (
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700">Meal length (unpaid, minutes)</label>
                            <input
                              type="number"
                              min={MIN_UNPAID_MEAL_MINUTES}
                              max={MAX_UNPAID_MEAL_MINUTES}
                              step={15}
                              value={addShiftUnpaidMealMinutes}
                              onChange={(e) => {
                                const raw = parseInt(e.target.value, 10);
                                if (!Number.isFinite(raw)) return;
                                const mins = clampUnpaidMealMinutes(raw);
                                setAddShiftUnpaidMealMinutes(mins);
                                setAddShiftMealBreakStart(suggestMealBreakStart(addShiftStart, addShiftEnd, mins));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                            />
                            <RosterQuarterHourTime label="Meal break starts" value={addShiftMealBreakStart} onChange={setAddShiftMealBreakStart} className="[&_select]:text-sm [&_select]:py-2 [&_select]:px-2" />
                            <p className="text-xs text-gray-500">{nzBreakComplianceHint(shiftWallMinutes(addShiftStart, addShiftEnd))}</p>
                            {nzTenPlusMealWarning(shiftWallMinutes(addShiftStart, addShiftEnd)) && <p className="text-xs text-amber-800 font-medium">10+ hours on site: a second unpaid meal may be required.</p>}
                          </div>
                        )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                          <select value={addShiftStatus} onChange={(e) => setAddShiftStatus(e.target.value as ShiftStatus)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white">
                            <option value="open">Open</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="confirmed">Confirmed</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                          <input type="text" value={addShiftNotes} onChange={(e) => setAddShiftNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="e.g. Split shift" />
                        </div>
                      </div>
                    </div>
                  )}
                  {addShiftStep === 'summary' && (
                    <div className="space-y-3">
                      <h3 className="text-2xl font-semibold text-gray-900">{editingShiftId ? 'Edit summary' : 'Shift summary'}</h3>
                      <p className="text-sm text-gray-700">
                        Employee: <strong>{addShiftStaffName}</strong>
                      </p>
                      <p className="text-sm text-gray-700">
                        Shift will be created for <strong className="capitalize">{addShiftDay}</strong>, <strong>{addShiftStart}-{addShiftEnd}</strong>.
                      </p>
                      <p className="text-xs text-gray-500">
                        Total paid hours: {paidHoursRounded({ start: addShiftStart, end: addShiftEnd, unpaidMealMinutes: addShiftUnpaidMealMinutes } as Shift)}h
                      </p>
                    </div>
                  )}
                  <div className="mt-6 flex flex-wrap gap-2 justify-between">
                    <div className="flex gap-2">
                      <button type="button" onClick={closeAddShiftModal} className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                        Cancel
                      </button>
                      {editingShiftId && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!window.confirm('Delete this shift?')) return;
                            handleDeleteShift(editingShiftId);
                            closeAddShiftModal();
                          }}
                          className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50"
                        >
                          Delete shift
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {addShiftStep !== 'create' && (
                        <button type="button" onClick={() => setAddShiftStep('create')} className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                          Back
                        </button>
                      )}
                      {addShiftStep !== 'summary' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAddShiftStep('summary');
                          }}
                          className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600"
                        >
                          Continue
                        </button>
                      ) : (
                        <button type="button" onClick={submitAddShiftModal} className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600">
                          {editingShiftId ? 'Save changes' : 'Add shift'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'timesheets' ? (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-4 flex flex-col sm:flex-row flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={formatDateInput(tsStartDate)}
                  onChange={(e) => setTsStartDate(e.target.value ? new Date(e.target.value) : tsStartDate)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={formatDateInput(tsEndDate)}
                  onChange={(e) => setTsEndDate(e.target.value ? new Date(e.target.value) : tsEndDate)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
                <select
                  value={tsStaffFilter}
                  onChange={(e) => setTsStaffFilter(e.target.value === '' ? '' : Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">All staff</option>
                  {staffMembers.map((m) => (
                    <option key={(m as { firestoreId?: string }).firestoreId ?? `staff-${m.id}`} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={loadTimesheetEntries}
                className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 text-sm font-medium"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={() => {
                  const header = ['Date', 'Staff name', 'Start (24h)', 'End (24h)', 'Notes'];
                  const rows = staffMembers.map((m) => [
                    '', // Date - fill in YYYY-MM-DD
                    m.name,
                    '09:00',
                    '17:00',
                    '',
                  ]);
                  const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = 'timesheet-import-template.csv';
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
                title="Download CSV with all staff names; fill in Date and times then Import CSV"
              >
                Download template
              </button>
              <label className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium cursor-pointer" title="CSV columns: Date (YYYY-MM-DD), Staff name, Start (24h), End (24h), Notes">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setTsImportFile(f || null);
                    if (f) setTsImportResult(null);
                    e.target.value = '';
                  }}
                />
                Import CSV
              </label>
              {tsImportFile && (
                <button
                  type="button"
                  onClick={handleTimesheetImportCSV}
                  disabled={tsImportLoading}
                  className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-50 text-sm font-medium"
                >
                  {tsImportLoading ? 'Importing…' : `Upload ${tsImportFile.name}`}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  const periodStart = formatDateInput(tsStartDate);
                  const periodEnd = formatDateInput(tsEndDate);
                  const exportedAt = new Date().toISOString();
                  const headerRows = [
                    ['Timesheet Export', ''],
                    ['Period start', periodStart],
                    ['Period end', periodEnd],
                    ['Exported at', exportedAt],
                    [''],
                    ['Date', 'Staff name', 'Start (24h)', 'End (24h)', 'On site (h)', 'Paid (h)', 'Source', 'Notes'],
                  ];
                  const dataRows = [
                    ...rosterDerivedRows.map((r) => [
                      formatDateInput(new Date(r.date)),
                      r.staffName,
                      r.start,
                      r.end,
                      r.onSiteHours.toFixed(1),
                      r.actualHours.toFixed(1),
                      'Roster',
                      r.notes || '',
                    ]),
                    ...manualEntries.map((e) => {
                      const staff = getStaffForTimesheetEntry(e, staffMembers);
                      const onSite = scheduledHoursFromShift(e.startTime, e.endTime);
                      const paid = e.actualHours ?? onSite;
                      return [
                        formatDateInput(new Date(e.date)),
                        staff?.name ?? String(e.staffId),
                        e.startTime,
                        e.endTime,
                        onSite.toFixed(1),
                        paid.toFixed(1),
                        'Manual',
                        e.notes || '',
                      ];
                    }),
                  ];
                  const rows = [...headerRows, ...dataRows];
                  const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `timesheet-${periodStart}-to-${periodEnd}.csv`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
              >
                Export CSV
              </button>
            </div>
            {tsImportResult && (
              <div className={`mb-4 p-3 rounded-lg border text-sm ${tsImportResult.failed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <p className="font-medium">
                  {tsImportResult.imported > 0 && <span className="text-green-800">Imported {tsImportResult.imported} entries.</span>}
                  {tsImportResult.failed > 0 && <span className="text-amber-800"> {tsImportResult.failed} row(s) failed.</span>}
                </p>
                {tsImportResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-amber-800 text-xs">
                    {tsImportResult.errors.map((msg, idx) => <li key={idx}>{msg}</li>)}
                  </ul>
                )}
                <button type="button" onClick={() => setTsImportResult(null)} className="mt-2 text-xs text-gray-600 underline">Dismiss</button>
              </div>
            )}
            {timesheetTotalsByStaff.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">Hours by staff (period)</p>
                <div className="flex flex-wrap gap-4">
                  {timesheetTotalsByStaff.map(({ staffKey, name, hours }) => (
                    <span key={`ts-total-${staffKey}`} className="text-sm font-medium text-gray-900">{name}: {hours.toFixed(1)}h</span>
                  ))}
                  <span className="text-sm text-gray-500">
                    Total: {timesheetTotalsByStaff.reduce((a, b) => a + b.hours, 0).toFixed(1)}h
                  </span>
                </div>
              </div>
            )}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <p className="text-sm text-gray-500 px-4 py-2 bg-gray-50 border-b border-gray-200">
                Roster shows planned times only. Adjust <strong>actual hours</strong> here for late starts or extra work—so copying next week&apos;s roster keeps the correct planned times.
              </p>
              {tsLoading ? (
                <p className="p-4 text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rosterDerivedRows.map((r, idx) => (
                        <tr key={`roster-${r.shiftId}-${r.weekStart}-${r.date}-${idx}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(r.date).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{r.staffName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{r.start} – {r.end}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              min={0}
                              step={0.5}
                              value={r.actualHours}
                              onChange={(e) => {
                                const v = Math.max(0, Number(e.target.value));
                                if (!Number.isNaN(v)) setRosterDerivedRows((prev) => prev.map((row) => (row.shiftId === r.shiftId ? { ...row, actualHours: v } : row)));
                              }}
                              onBlur={(e) => {
                                const v = Math.max(0, Number((e.target as HTMLInputElement).value));
                                if (!Number.isNaN(v)) updateRosterShiftActualHours(r.shiftId, r.weekStart, v);
                              }}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                              title="Actual hours (saved on blur)"
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap"><span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">Roster</span></td>
                          <td className="px-4 py-3 text-sm text-gray-500">{r.notes || '—'}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-400">—</td>
                        </tr>
                      ))}
                      {manualEntries.map((e) => {
                        const staff = getStaffForTimesheetEntry(e, staffMembers);
                        const hours = e.actualHours ?? scheduledHoursFromShift(e.startTime, e.endTime);
                        return (
                          <tr key={e.id}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {new Date(e.date).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{staff?.name ?? e.staffId}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{e.startTime} – {e.endTime}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{hours.toFixed(1)}</td>
                            <td className="px-4 py-3 whitespace-nowrap"><span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Manual</span></td>
                            <td className="px-4 py-3 text-sm text-gray-500">{e.notes || '—'}</td>
                            <td className="px-4 py-3 text-right text-sm">
                              <button onClick={() => handleDeleteTimesheetEntry(e.id)} className="text-red-600 hover:text-red-900" title="Delete"> <TrashIcon className="w-4 h-4" /> </button>
                            </td>
                          </tr>
                        );
                      })}
                      {rosterDerivedRows.length === 0 && manualEntries.length === 0 && !tsLoading && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No entries in this period. Add manual entries with the button above, or assign staff in the Roster tab.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'payslips' && canViewPayroll ? (
          <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Payslip generation</h2>
            <p className="text-sm text-gray-500 mb-4">Enter pay period and hours (or fill from Timesheet). Set tax code and hourly rate per staff, then Calculate. Export to PDF per payslip or all.</p>

            <div className="flex flex-wrap items-end gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay period start</label>
                <input
                  type="date"
                  value={formatDateInput(payPeriodStart)}
                  onChange={(e) => setPayPeriodStart(new Date(e.target.value + 'T00:00:00'))}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay period end</label>
                <input
                  type="date"
                  value={formatDateInput(payPeriodEnd)}
                  onChange={(e) => setPayPeriodEnd(new Date(e.target.value + 'T23:59:59'))}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay frequency</label>
                <select
                  value={payslipPayFrequency}
                  onChange={(e) => setPayslipPayFrequency(e.target.value as PayFrequency)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 text-sm"
                >
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(1);
                    d.setHours(0, 0, 0, 0);
                    setPayPeriodStart(d);
                    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                    e.setHours(23, 59, 59, 999);
                    setPayPeriodEnd(e);
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  This month
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - 1);
                    d.setDate(1);
                    d.setHours(0, 0, 0, 0);
                    setPayPeriodStart(d);
                    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                    e.setHours(23, 59, 59, 999);
                    setPayPeriodEnd(e);
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Last month
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const end = new Date();
                    end.setHours(23, 59, 59, 999);
                    setPayPeriodEnd(end);
                    const start = new Date(end);
                    start.setDate(start.getDate() - 13);
                    start.setHours(0, 0, 0, 0);
                    setPayPeriodStart(start);
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Last 2 weeks
                </button>
              </div>
              <button
                type="button"
                onClick={handlePayslipFillFromTimesheet}
                disabled={payslipFillLoading}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
              >
                {payslipFillLoading ? 'Loading…' : 'Fill hours from Timesheet'}
              </button>
              <button
                type="button"
                onClick={handlePayslipCalculate}
                className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 text-sm font-medium"
              >
                Calculate
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Work type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tax code</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hourly rate</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Annual leave %</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Holiday pay</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gross</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">PAYE</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payslipRows.map((row) => (
                    <tr key={row.key}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">{row.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {row.workType === 'full_time' ? 'Full time' : row.workType === 'part_time' ? 'Part time' : row.workType === 'casual' ? 'Casual' : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={row.hours || ''}
                          onChange={(e) => setPayslipRow(row.key, (r) => ({ ...r, hours: parseFloat(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={row.taxCode}
                          onChange={(e) => setPayslipRow(row.key, (r) => ({ ...r, taxCode: e.target.value }))}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="M">M</option>
                          <option value="ME">ME</option>
                          <option value="M SL">M SL</option>
                          <option value="ME SL">ME SL</option>
                          <option value="SB">SB</option>
                          <option value="S">S</option>
                          <option value="SH">SH</option>
                          <option value="ST">ST</option>
                          <option value="SA">SA</option>
                          <option value="SB SL">SB SL</option>
                          <option value="S SL">S SL</option>
                          <option value="SH SL">SH SL</option>
                          <option value="ST SL">ST SL</option>
                          <option value="SA SL">SA SL</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={row.hourlyRate || ''}
                          onChange={(e) => setPayslipRow(row.key, (r) => ({ ...r, hourlyRate: parseFloat(e.target.value) || 0 }))}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.workType === 'casual' ? (
                          <input
                            type="number"
                            min={0}
                            max={1}
                            step={0.01}
                            value={row.annualLeaveRate ?? 0.08}
                            onChange={(e) => setPayslipRow(row.key, (r) => ({ ...r, annualLeaveRate: parseFloat(e.target.value) || 0.08 }))}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                          />
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-gray-900">
                        {row.workType === 'casual' ? (row.holidayPay != null ? `$${row.holidayPay.toFixed(2)}` : '—') : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-gray-900">{row.gross != null ? `$${row.gross.toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2 text-right text-sm text-gray-900">{row.paye != null ? `$${row.paye.toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{row.net != null ? `$${row.net.toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2 text-center text-xs">
                        <select
                          value={row.status ?? 'draft'}
                          onChange={async (e) => {
                            const nextStatus = e.target.value as 'draft' | 'processed';
                            let updatedRow: PayslipRow;
                            setPayslipRow(row.key, (r) => {
                              updatedRow = { ...r, status: nextStatus };
                              return updatedRow;
                            });
                            // Persist if marked as processed
                            if (nextStatus === 'processed') {
                              await persistPayslipIfProcessed({
                                ...row,
                                status: 'processed',
                              });
                            }
                          }}
                          className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                        >
                          <option value="draft">Draft</option>
                          <option value="processed">Processed</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right space-x-3">
                        <button
                          type="button"
                          onClick={async () => {
                            const staff = staffMembers.find((s) => s.id === row.staffId);
                            const toEmail = staff?.personalEmail || staff?.email;
                            if (!toEmail) {
                              alert(`No email set for ${staff?.name ?? row.name}.`);
                              return;
                            }
                            if (row.gross == null || row.paye == null || row.net == null) {
                              alert('Please calculate the payslip before emailing.');
                              return;
                            }
                            try {
                              const { success, error } = await emailService.sendStaffPayslip(toEmail, {
                                staffName: staff?.name ?? row.name,
                                payPeriodStart: formatDateInput(payPeriodStart),
                                payPeriodEnd: formatDateInput(payPeriodEnd),
                                hours: row.hours,
                                hourlyRate: row.hourlyRate,
                                holidayPay: row.holidayPay,
                                gross: row.gross,
                                paye: row.paye,
                                net: row.net,
                                businessInfo,
                              });
                              if (!success) {
                                alert(error || 'Failed to send payslip email.');
                                return;
                              }
                              setPayslipRow(row.key, (r) => ({ ...r, status: 'processed' }));
                              await persistPayslipIfProcessed({ ...row, status: 'processed' });
                              alert(`Payslip emailed to ${toEmail}.`);
                            } catch (e) {
                              console.error('Error sending payslip email', e);
                              alert('Failed to send payslip email.');
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Email
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (row.gross == null || row.paye == null || row.net == null) return;
                            const logoDataUrl = await loadPayslipLogoDataUrl();
                            generatePayslipPdf({
                              staffName: row.name,
                              payPeriodStart: formatDateInput(payPeriodStart),
                              payPeriodEnd: formatDateInput(payPeriodEnd),
                              hours: row.hours,
                              hourlyRate: row.hourlyRate,
                              gross: row.gross,
                              paye: row.paye,
                              net: row.net,
                              holidayPay: row.holidayPay,
                              taxCode: row.taxCode || undefined,
                              businessName: businessInfo?.businessName ?? 'KINGS PARK',
                              businessAddress: businessInfo?.address,
                              businessGstNumber: businessInfo?.gstNumber,
                              businessNzbn: businessInfo?.nzbn,
                              logoDataUrl,
                              logoAspectRatio: payslipLogoAspectRatioRef.current ?? undefined,
                            });
                          }}
                          disabled={row.gross == null}
                          className="text-pink-600 hover:text-pink-800 text-sm font-medium disabled:opacity-50"
                        >
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {payslipRows.some((r) => r.gross != null) && (
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="text-sm font-medium text-gray-700">
                  Total holiday pay: ${payslipRows.reduce((a, r) => a + (r.holidayPay ?? 0), 0).toFixed(2)}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  Total PAYE: ${payslipRows.reduce((a, r) => a + (r.paye ?? 0), 0).toFixed(2)}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  Total net: ${payslipRows.reduce((a, r) => a + (r.net ?? 0), 0).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    const logoDataUrl = await loadPayslipLogoDataUrl();
                    const list = payslipRows
                      .filter((r) => r.gross != null && r.paye != null && r.net != null)
                      .map((row) => ({
                        staffName: row.name,
                        payPeriodStart: formatDateInput(payPeriodStart),
                        payPeriodEnd: formatDateInput(payPeriodEnd),
                        hours: row.hours,
                        hourlyRate: row.hourlyRate,
                        gross: row.gross!,
                        paye: row.paye!,
                        net: row.net!,
                        holidayPay: row.holidayPay,
                        taxCode: row.taxCode || undefined,
                        businessName: businessInfo?.businessName ?? 'KINGS PARK',
                        businessAddress: businessInfo?.address,
                        businessGstNumber: businessInfo?.gstNumber,
                        businessNzbn: businessInfo?.nzbn,
                        logoDataUrl,
                        logoAspectRatio: payslipLogoAspectRatioRef.current ?? undefined,
                      }));
                    generatePayslipsBatchPdf(
                      list,
                      businessInfo?.businessName ?? 'KINGS PARK',
                      `payslips-${formatDateInput(payPeriodStart)}-to-${formatDateInput(payPeriodEnd)}.pdf`
                    );
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 text-sm font-medium"
                >
                  Download all payslips (PDF)
                </button>
              </div>
            )}
          </div>
        ) : null}
      </main>
      {isModalOpen && (
        <StaffMemberModal
          member={editingMember}
          userRoles={userRoles}
          onClose={handleCloseModal}
          onSave={handleSave}
          canViewPrivateHrPay={
            isAdminUser || canViewPayroll || (!editingMember?.hrDetailsLocked && canManageStaff)
          }
          canSetHrLock={canSetHrLock}
          canViewSensitive={canViewPins}
        />
      )}
      {tsShowAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setTsShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">Add timesheet entry</h2>
            <p className="text-sm text-gray-500">Use for overtime or shifts not on the roster.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
              <select
                value={tsAddStaffKey}
                onChange={(e) => setTsAddStaffKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="">Select...</option>
                {staffMembers.map((m) => (
                  <option key={(m as { firestoreId?: string }).firestoreId ?? `staff-${m.id}`} value={(m as { firestoreId?: string }).firestoreId ?? `staff-${m.id}`}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={tsAddDate}
                onChange={(e) => setTsAddDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start (24-hour)</label>
                <input
                  type="time"
                  value={tsAddStart}
                  onChange={(e) => setTsAddStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End (24-hour)</label>
                <input
                  type="time"
                  value={tsAddEnd}
                  onChange={(e) => setTsAddEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {(() => {
                const span = scheduledHoursFromShift(tsAddStart, tsAddEnd);
                const unpaid = tsAddUnpaidBreakMinutes.trim() === '' ? 0 : Math.max(0, Number(tsAddUnpaidBreakMinutes));
                const afterBreak = Math.max(0, span - unpaid / 60);
                return `Span: ${span.toFixed(1)}h · After unpaid break: ${afterBreak.toFixed(1)}h`;
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unpaid break (minutes)</label>
              <input
                type="number"
                min="0"
                step="5"
                value={tsAddUnpaidBreakMinutes}
                onChange={(e) => setTsAddUnpaidBreakMinutes(e.target.value)}
                placeholder="e.g. 30 for 1 x 30-min meal"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500 mb-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual hours (optional override)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={tsAddActualHours}
                placeholder="Leave blank to use span minus unpaid break"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                data-soft-keyboard-handled
                {...tsActualHoursKeyboard}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={tsAddNotes}
                placeholder="e.g. Overtime, training"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                data-soft-keyboard-handled
                {...tsNotesKeyboard}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setTsShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitTimesheetEntry}
                disabled={tsAddSaving}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50"
              >
                {tsAddSaving ? 'Saving...' : 'Add entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagementPage;